import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  factsFromAdminBooking,
  factsFromPending,
  useOwnerNotesPrompt,
  type OwnerNotesPrompt,
} from '../components/booking-notes/index.js';
import { Link, useSearchParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import {
  adminPaymentsApi,
  buildTierConfirmationPayload,
  cancelBooking,
  checkInBooking,
  undoCheckInBooking,
  undoCheckOutBooking,
  checkOutBooking,
  checkOutBookingSettleLater,
  checkOutGroomingBooking,
  fetchAdminSchedule,
  fetchOutstandingBookings,
  fetchRunCards,
  recordRunCardsPrinted,
  fetchServiceTypes,
  humanizeApiError,
  readSession,
  todayIsoDate,
  ApiError,
  isArrivalUnverified,
  type AdminBooking,
  type AdminLatePickup,
  type AdminLatePickupDisposition,
  type AdminOpenInvoice,
  type AdminTransaction,
  type ServiceTypeSummary,
} from '../lib/api.js';
import { TierConfirmation } from '../components/checkout/TierConfirmation.js';
// Sawyer 13 household-checkout-grooming (C3)
import { selectedRequestedAddOnIds } from '../components/checkout/RequestedAddOns.js';
import { useRequestedAddOns } from '../components/checkout/useRequestedAddOns.js';
import { applyRequestedAddOns } from '../lib/requested-add-ons-api.js';
import {
  groupCheckoutHouseholds,
  householdGroomingNote,
  type HouseholdGroup as HouseholdGroupOf,
} from '../lib/household-checkout.js';
import {
  CheckoutActions,
  type CheckoutAction,
} from '../components/checkout/CheckoutActions.js';
import { SettleNowDialog } from '../components/checkout/SettleNowDialog.js';
// Ted #32c (#1158) — the cancel confirm, the Edit dialog and No-show, shared
// with the Front Desk and dashboard Expected rows (one set of dialogs, one set
// of words). The Bookings page keeps its own optimistic cancel (`performCancel`).
import {
  useReservationActions,
  type ReservationTarget,
} from '../components/booking/useReservationActions.js';
// Ted #32c (#1160) — the one family picker, shared with Front Desk + dashboard.
import {
  FamilyCheckInDialog,
  type FamilyCheckInMember,
} from '../components/check-in/FamilyCheckInDialog.js';
// Ted #30 (#1109, lane LR) — the series view from a row. The #986 scope
// question before an occurrence's edit lives in useReservationActions, so the
// Front Desk and dashboard Expected rows ask it too.
import { RecurringSeriesPanel } from '../components/booking/RecurringSeriesPanel.js';
import { GroomerAssign } from '../components/booking/GroomerAssign.js';
// Ted #26 (#987) — batch confirm with individual review.
import { ClientNotNotifiedPill } from '../components/booking/ClientNotNotifiedPill.js';
// Sawyer 10 customer-booking-verify-policy (C5 C6) — the owner's attestations at booking.
import { OwnerCareCheckPill } from '../components/booking/OwnerCareCheckPill.js';
import { ConfirmPendingDialog } from '../components/booking/ConfirmPendingDialog.js';
import {
  bookingWhenLabel,
  sortOldestFirst,
  toPendingReservation,
} from '../components/booking/confirm-pending.js';
import {
  isoOffsetDays,
  pendingWindow,
  useConfirmPending,
} from '../components/booking/useConfirmPending.js';
import { AddServiceDialog } from '../components/booking/AddServiceDialog.js';
// Ted #29 (#1108) — one date of an added service, editable on its own.
import { ServiceLegLine } from '../components/booking/ServiceLegLine.js';
import { DogFirstCell, useArrivalVerification } from '../components/dog-first/index.js';
// Ted 09-24 / 09-29 (lane LC) — check-in is the LOUD moment, checkout stays
// quiet: "Before you check in" (flags acknowledged one by one, Do Not Admit
// as a stop, the heat question), the money-only "Before you check out", and
// the mid-stay "In heat" action. One component each, every surface.
import { useCheckInInterrupt } from '../components/check-in/useCheckInInterrupt.js';
import { useCheckoutInterrupt } from '../components/check-in/CheckoutInterruptDialog.js';
import { useInHeatAction } from '../components/check-in/InHeatDialog.js';
import {
  describeStopped,
  interruptRowFromAdminBooking,
  planCheckInInterrupt,
  planCheckoutInterrupt,
  type InterruptDecision,
} from '../components/check-in/check-in-interrupt.js';
import { heatCheckAppliesFromCard } from '@ferro/core/modules/dog-profile/dog-flags.js';
import {
  isGroomingServiceType,
  type GroomingTierKey,
} from '../lib/groomingTiers.js';
import {
  RunCardPrintLayout,
  type RunCardData,
} from '../components/run-cards/RunCardPrintLayout.js';
import { apiCardToRunCard } from '../components/run-cards/api-card-to-run-card.js';
import { useRunCardPrompt } from '../components/run-cards/useRunCardPrompt.js';
import {
  PackageBalance,
  shouldOfferCourtesyReminder,
  isExpired as isPackageExpired,
  type CustomerPackage,
  type PackageServiceKind,
} from '../components/checkout/PackageBalance.js';
import { makeIdempotencyKey } from '../lib/idempotency.js';
import {
  DEFAULT_PACKAGE_CHOICE,
  PackageApplyControl,
  resolveChosenPackage,
  type PackageChoice,
} from '../components/checkout/PackageApplyControl.js';
// #1157 E2.4 — one package day per dog, and the household total after the package.
import {
  dogCountOf,
  householdDueAfterPackages,
  householdMoneyLabel,
  packageDaysForVisit,
  packageKey,
  packageKindForService,
  stayWindowOf,
} from '../components/checkout/package-days.js';
// #1157 E2.5 — a corrected checkout time decides the late pickup.
import { applyCorrectedCheckout } from '../components/checkout/late-pickup-correction.js';
import {
  LatePickupControl,
  defaultLatePickupChoice,
  latePickupDisposition,
  type LatePickupChoice,
} from '../components/checkout/LatePickupControl.js';
import { isBoardingServiceName } from '../components/check-in/CheckInCareSheet.js';
import {
  checkInHousehold,
  describeHouseholdCheckIn,
  segmentByHousehold,
  type HouseholdRow,
} from '../components/dashboard/household-checkin.js';
import {
  correctedArrivalVisible,
  correctedCheckoutVisible,
  correctedInstantToSend,
  daysOverdue,
  formatServiceDay,
  lastServiceDayIso,
  outstandingArrivalMin,
  seedOutstandingCheckinTimes,
  seedOutstandingCheckoutTimes,
  sourceLabel,
} from '../lib/outstanding.js';

type BookingsProps = {
  config: ClientConfig;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; forbidden: boolean }
  | { kind: 'ready'; bookings: AdminBooking[] };

type Action =
  | 'confirm'
  | 'check-in'
  | 'check-out'
  | 'cancel'
  | 'undo-check-in'
  | 'undo-check-out'
  | 'edit'
  | 'no-show';

/**
 * Ted #27 part 2 (#988) — which list the page is showing. NOT a fifth
 * surface: the same rows, the same actions, the same money path; only the
 * question changes from "what is on this day" to "what did we never
 * finish".
 */
type BookingsView = 'day' | 'outstanding';

/** How many outstanding rows one page of the list holds. */
const OUTSTANDING_PAGE_SIZE = 200;

/**
 * The tab badge's source of truth — the server's own count, never derived
 * from the rendered rows. Loading and error are distinct states because a
 * count that failed to load must NEVER render as "0 outstanding"
 * (Design Philosophy, Principle 5).
 */
type OutstandingMeta =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | {
      readonly kind: 'ready';
      readonly total: number;
      /** The facility day the server measured "past" against (YYYY-MM-DD). */
      readonly before: string;
      readonly timezone: string;
    };

/**
 * Ted #11 (#914) — the late-pickup proposal for one row. Three states, never
 * collapsed: a failed read is NOT "no late charge" (Principle 5), so the row
 * says so and offers Retry rather than rendering nothing.
 */
type LatePickupState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly proposal: AdminLatePickup }
  | { readonly status: 'error'; readonly message: string };

interface TierConfirmDialogState {
  readonly booking: AdminBooking;
  readonly serviceType: ServiceTypeSummary | null;
  readonly bookingLabel: string;
  readonly originalTierKey: GroomingTierKey | null;
  readonly submitting: boolean;
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function shortId(id: string): string {
  return id.slice(0, 8);
}

/**
 * Post-void booking coherence (money-path audit P1). From the desk
 * transactions feed, find bookings whose payment(s) are ALL reversed
 * (voided or refunded) with NO completed payment remaining — the state
 * where a booking still reads confirmed/paid but the money was pulled
 * back. Keyed bookingId → reversal kind (a refund on any leg dominates a
 * void for the message copy).
 *
 * Best-effort by design: the feed is tenant-wide and capped (limit 100),
 * so a booking whose reversal predates the window simply yields no entry —
 * the banner just doesn't render, never a false claim. The feed only ever
 * returns completed+refunded rows, so "no completed row for this booking"
 * is sufficient to conclude nothing is still paid.
 */
export function deriveReversedBookings(
  transactions: ReadonlyArray<AdminTransaction>,
): Map<string, 'voided' | 'refunded'> {
  const byBooking = new Map<
    string,
    { hasCompleted: boolean; refunds: number; voids: number }
  >();
  for (const t of transactions) {
    if (!t.bookingId) continue;
    const acc = byBooking.get(t.bookingId) ?? {
      hasCompleted: false,
      refunds: 0,
      voids: 0,
    };
    if (t.status === 'completed') {
      acc.hasCompleted = true;
    } else if (t.status === 'refunded') {
      // Prefer the server's derived reversal; fall back to refunded_cents
      // (a void never increments it) for deploy skew.
      const isRefund =
        t.reversal === 'refunded' ||
        (t.reversal == null && t.refundedCents > 0);
      if (isRefund) acc.refunds += 1;
      else acc.voids += 1;
    }
    byBooking.set(t.bookingId, acc);
  }
  const out = new Map<string, 'voided' | 'refunded'>();
  for (const [bookingId, acc] of byBooking) {
    if (acc.hasCompleted) continue;
    if (acc.refunds + acc.voids === 0) continue;
    out.set(bookingId, acc.refunds > 0 ? 'refunded' : 'voided');
  }
  return out;
}

function statusLabel(status: string): string {
  // Ted #32c (#1158) — "reads No-show in By day": the closed decision gets its
  // own word, hyphenated like the button that made it.
  if (status === 'no_show') return 'No-show';
  return status.replace(/_/g, ' ');
}

/**
 * Ted #32c / E3.3 (#1158) — FIND A VISIT. The Bookings list loads one date, so a
 * checked-out visit from last Tuesday was findable only by guessing its date
 * (Ted, 10-06: "After checkout, those options aren't easy to find either").
 * Two ways in, both of which read a window of dates instead of one:
 *   - a search box — dog or owner name, over the last two months and the next one;
 *   - "Recently checked out" — every visit checked out in the last month.
 * Neither changes what a row can do: a checked-out row still has exactly one
 * action, Reopen checkout (direct edit of a checked-out visit would rewrite
 * money — reopen, change, check out again, which works today).
 */
export const FIND_LOOKBACK_DAYS = 60;
export const FIND_LOOKAHEAD_DAYS = 30;
export const RECENT_CHECKOUT_DAYS = 30;
/** Two characters before a search reads a window — one letter matches half the building. */
export const FIND_MIN_CHARS = 2;

/** The dates a find reads: recently-checked-out is the past month, a search is -60/+30 days. */
export function findWindow(recentlyCheckedOut: boolean): {
  startDate: string;
  endDate: string;
  status?: string;
} {
  return recentlyCheckedOut
    ? {
        startDate: isoOffsetDays(-RECENT_CHECKOUT_DAYS),
        endDate: isoOffsetDays(0),
        status: 'checked_out',
      }
    : {
        startDate: isoOffsetDays(-FIND_LOOKBACK_DAYS),
        endDate: isoOffsetDays(FIND_LOOKAHEAD_DAYS),
      };
}

/** Does this row belong to a dog or owner the desk typed? Case-insensitive, substring. */
export function bookingMatchesSearch(b: AdminBooking, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  if ((b.customer_name ?? '').toLowerCase().includes(needle)) return true;
  return (b.dog_names ?? []).some((name) => name.toLowerCase().includes(needle));
}

/** Newest visit first: a find reads as "what happened lately", not "what is on at 7am". */
function sortNewestFirst(rows: readonly AdminBooking[]): AdminBooking[] {
  return [...rows].sort((a, b) => {
    const byDay = (b.start_date ?? b.date ?? '').localeCompare(a.start_date ?? a.date ?? '');
    return byDay !== 0 ? byDay : (a.start_time ?? '').localeCompare(b.start_time ?? '');
  });
}

/**
 * Ted #32c (#1158) — has this reservation's first service day arrived? A
 * reservation for a day that has not started cannot be a no-show (the server
 * refuses it and says "cancel it instead"), so the row does not offer the
 * button. The SERVER stays the authority — it measures the facility's day, this
 * only keeps a pointless tap off the screen.
 */
function firstDayReached(b: AdminBooking, today: string): boolean {
  const first = (b.start_date ?? b.date ?? '').slice(0, 10);
  return first.length === 10 && first <= today;
}

// ---------- Run cards (Becky-T1.5 · issue #27) ----------

function isBoardingServiceType(name: string | null | undefined): boolean {
  if (!name) return false;
  const n = name.toLowerCase();
  return n.includes('board') || n.includes('overnight') || n.includes('lodging');
}


/**
 * Ted #23 (#984) 09-16 re-review — `isLeg` is the second argument because a
 * SERVICE LEG has no lifecycle of its own. Its parent's check-in, undo,
 * check-out and cancel all cascade to it (`cascadeToServiceLegs`), so a leg
 * row offering those actions let the desk move the nail trim WITHOUT moving
 * Milo's visit: the leg read "checked in" while the daycare booking stayed
 * `confirmed` and the dog was never on the floor. Check Out was the same fault
 * with money attached — it would settle the leg's own invoice and leave the
 * visit's behind, breaking the itemized single checkout this PR exists to
 * give Ted. `cancel` STAYS: dropping an added service is a real desk decision
 * that legitimately touches only this row (`findBillableServiceLegs` already
 * excludes a cancelled leg from billing, pinned by block E).
 */
function nextActions(status: string, isLeg = false): Action[] {
  if (isLeg) return status === 'cancelled' ? [] : ['cancel'];
  switch (status) {
    // Ted #26 (#987) — a pending reservation can be CONFIRMED from the row.
    // Until this change the admin app had no confirm caller at all: its only
    // helper pointed at the unmounted /api/v2/admin/bookings/:id/confirm, so
    // every staff-created booking sat pending with nothing to move it.
    case 'pending':
      // Ted #25 — a PENDING reservation is editable too (it is in
      // EDITABLE_BOOKING_STATUSES), and the online mistakes Ted described
      // arrive pending. Rebase note: #1007 replaced this arm wholesale to add
      // Confirm; dropping Edit here would have quietly narrowed #986 to
      // confirmed-and-later rows.
      return ['confirm', 'check-in', 'edit', 'no-show', 'cancel'];
    case 'confirmed':
      // Ted #25 — "before check-in and during the check-in process."
      // Ted #32c (#1158) — and the dog that never came: No-show closes the row
      // (no charge, a reason on record). Only a dog that has NOT arrived.
      return ['check-in', 'edit', 'no-show', 'cancel'];
    case 'checked_in':
      // Ted #16 — a mistaken check-in is reversible from the row.
      // Ted #25 — and the service/date/times are still correctable after the
      // dog is on the floor, which is exactly when the desk notices.
      return ['check-out', 'edit', 'undo-check-in', 'cancel'];
    case 'checked_out':
      // Ted #22 (#983) — "Juniper then disappeared from the checkout area."
      // She did not: the row lost every action the moment it was checked
      // out, which is the same thing from the counter. One action back.
      // No Edit here: `checked_out` is not in EDITABLE_BOOKING_STATUSES, and
      // Ted #25's server refuses it 409 `booking_not_editable`.
      return ['undo-check-out'];
    default:
      return [];
  }
}

const ACTION_LABEL: Record<Action, string> = {
  confirm: 'Confirm',
  'check-in': 'Check In',
  'check-out': 'Check Out',
  edit: 'Edit',
  'undo-check-in': 'Undo check-in',
  'undo-check-out': 'Reopen checkout',
  'no-show': 'No-show',
  cancel: 'Cancel',
};

// ---------- Package balance (Becky-T1.3 · issue #26) ----------

// `packageKindForService` / `packageKey` live in
// components/checkout/package-days.ts (#1157 E2.4), with the per-dog rule.

/**
 * Ted #5 (#908) — the packages a checkout may draw on: days left, oldest
 * purchase first (the one the desk applies by default). Tolerates a
 * response missing the array.
 */
function activePackagesOf(packages: unknown): CustomerPackage[] {
  const list = Array.isArray(packages) ? (packages as CustomerPackage[]) : [];
  return list
    // #1157 E2.6 — an expired package is not offered (the server refuses it).
    .filter((p) => p.remainingDays > 0 && !isPackageExpired(p.expiresOn))
    .sort((a, b) => String(a.purchasedAt).localeCompare(String(b.purchasedAt)));
}

// ---------- Money path (issue #251 / #252) ----------

/**
 * Outcome of the package-redemption leg of a checkout.
 * `indeterminate` (#251 should-fix 7): the redeem POST timed out / hit a
 * 5xx — the server MAY have decremented the package day. Treating that as
 * "not applied" double-charged the customer (lost day + full-price
 * invoice); the money path must HALT and tell staff to verify instead.
 */
type PackageRedemptionOutcome =
  /** `declined` (Ted #5, #908): a package exists but staff chose not to apply it. */
  | { readonly applied: false; readonly declined?: boolean }
  | {
      readonly applied: true;
      readonly post: CustomerPackage;
      /** #1157 — the days the SERVER drew (it derives one per dog itself). */
      readonly days: number;
    }
  | { readonly applied: 'indeterminate'; readonly error: string };

/**
 * Everything needed to (re)create the money record for a checked-out
 * booking. Kept in state when the open-invoice POST fails so the blocking
 * failure dialog can retry the EXACT same record (issue #251 — a checkout
 * must never exit the money funnel silently).
 */
interface MoneyRecordArgs {
  readonly booking: AdminBooking;
  readonly bookingLabel: string;
  readonly track: CheckoutAction;
  readonly totalCents: number;
  readonly notes: string | null;
  /** True when a package day was already redeemed — settle as covered. */
  readonly covered: boolean;
  /** Ted #3 + #9 (#906 #912) — the reason the covered $0 invoice carries. */
  readonly coveredReason?: string | null;
  /**
   * Ted #11 (#914) — how the desk resolved a late boarding pickup. Carried on
   * the retry state too, so "Retry recording" after a failed money leg
   * re-sends the SAME disposition rather than quietly dropping the charge.
   */
  readonly latePickup?: AdminLatePickupDisposition;
}

interface MoneyRecordFailureState extends MoneyRecordArgs {
  readonly error: string;
}

/**
 * Ted #10 (#913) — how the money leg reports back. The single row lets the
 * leg open the settle dialog (or the blocking failure dialog) itself; the
 * household loop DEFERS both so it can run every sibling first and then
 * open ONE dialog for the family.
 */
interface MoneyLegOptions {
  readonly deferDialog?: boolean;
}

type MoneyLegResult =
  /** The settle-now dialog was opened for this visit (single-row flow). */
  | { readonly kind: 'dialog-opened' }
  /** Deferred: the invoice is queued; the caller opens the dialog. */
  | { readonly kind: 'invoice'; readonly invoice: AdminOpenInvoice }
  /** A package day covered the visit; its $0 invoice is settled. */
  | { readonly kind: 'covered' }
  /** Settle-later: the invoice waits in the end-of-day queue. */
  | { readonly kind: 'queued' }
  /** The invoice could not be written; `args` re-creates the exact record. */
  | { readonly kind: 'failed'; readonly args: MoneyRecordArgs; readonly error: string }
  /** Package redemption outcome unknown — the leg halted and raised its alert. */
  | { readonly kind: 'halted' };

/**
 * Ted #10 (#913) — one owner's checked-in visits, grouped on the list.
 * Sawyer C3 — the groom is in `rows` now; `groomingRowIds` names the visits
 * that get a tier step before "Check out all" posts anything.
 */
type HouseholdGroup = HouseholdGroupOf<AdminBooking>;

/** Sawyer C3 — one groom's confirmed tier step, held until the last step. */
interface HouseholdTierStepResult {
  readonly selectedTier: import('../lib/groomingTiers.js').GroomingTierDefinition;
  readonly originalTier: import('../lib/groomingTiers.js').GroomingTierDefinition;
  /** The TIER total only (refuter 3): ticked add-ons are billed as their own legs. */
  readonly finalTotalCents: number;
  readonly operatorNotes: string | null;
  /** The requested add-ons the desk left ticked for this groom. */
  readonly addOnIds: readonly string[];
  /** Their labels, captured at the step, for a failure sentence. */
  readonly addOnLabels: string;
}

/** Sawyer C3 — the inline tier steps of one household's "Check out all". */
interface HouseholdTierSteps {
  readonly household: HouseholdGroup;
  /** The household's grooming visits, list order. */
  readonly queue: readonly AdminBooking[];
  /** 0-based position in `queue`. */
  readonly index: number;
  readonly confirmed: ReadonlyMap<string, HouseholdTierStepResult>;
}

/**
 * Ted #2 follow-up (#905) + the check-in half of Ted #26 (#987) — a Bookings
 * row seen through the SHARED household lens
 * (`components/dashboard/household-checkin.ts`), so the Bookings list groups,
 * batches and words failures exactly as Dashboard Arrivals and Front Desk do.
 *
 * The grouping key is `customer_id` ONLY. This tenant stores dogs WITH the
 * family surname, so two of one owner's dogs share a surname AND an
 * unrelated customer's dog can carry the same one: a name key would
 * under-group the first pair and over-group the second. `booking` carries
 * the full row back for the actions.
 */
interface ArrivalRow extends HouseholdRow {
  readonly bookingId: string;
  readonly customerId: string;
  readonly customerName: string;
  readonly dogNames: string[];
  readonly booking: AdminBooking;
}

/** One owner's same-day dogs that are still to be checked in. */
export interface ArrivalHousehold {
  readonly customerId: string;
  readonly customerName: string;
  readonly rows: readonly ArrivalRow[];
}

/**
 * The statuses `nextActions` offers Check In for.
 *
 * Grooming is included here, and (Sawyer C3) on the #913 check-OUT card too:
 * checking OUT a grooming visit needs the per-visit tier confirmation
 * (basic vs profile changes what is charged), so "Check out all" collects
 * each groom's tier in an inline step BEFORE the batch posts anything.
 * Checking a dog IN writes `status` + `checked_in_at` and nothing else —
 * identical for every service — so a family with a groom and a daycare
 * arriving together is exactly the duplicate work Ted asked us to remove.
 */
function isArrivalStatus(status: string): boolean {
  return status === 'pending' || status === 'confirmed';
}

/**
 * Ted #2/#26 (#905/#987) — fold a single-booking ACTION response into the
 * row the list already holds, WITHOUT losing the dog-first display block.
 *
 * The block — `customer_name`, `dog_names`, `dogs`, `balance_due_cents`,
 * `unpaid` — is populated by `listSchedule` ONLY. Every other read path,
 * check-in included, still EMITS those keys, at their empty sentinels:
 * `bookingView` (packages/core/modules/booking/routes.ts) writes
 * `customer_name: b.customerName ?? null`, `dog_names: b.dogNames ?? []`,
 * `dogs: b.dogCards ?? []`, `balance_due_cents: 0`, `unpaid: false`, and
 * `updateBookingStatus` returns a bare `mapBooking` row that carries none
 * of them. So a shallow `{ ...row, ...updated }` does not protect the row:
 * the keys are present-but-empty, not absent, and the spread BLANKS the
 * dog photos, the "Dog — Owner" line and the red balance-due badge on
 * every row it touches.
 *
 * Hence: take the block from the response only when the response actually
 * carries it; otherwise keep what the list gave us and let the next
 * refresh reconcile. Never silently replace a known name with a blank.
 */
export function mergeActionResponse(
  row: AdminBooking,
  updated: AdminBooking,
): AdminBooking {
  const merged: AdminBooking = { ...row, ...updated };
  const carriesDisplayBlock =
    (updated.dogs?.length ?? 0) > 0 ||
    (updated.dog_names?.length ?? 0) > 0 ||
    (updated.customer_name ?? null) !== null;
  if (carriesDisplayBlock) return merged;
  return {
    ...merged,
    customer_name: row.customer_name,
    dog_names: row.dog_names,
    dogs: row.dogs,
    balance_due_cents: row.balance_due_cents,
    unpaid: row.unpaid,
  };
}

export function toArrivalRow(b: AdminBooking): ArrivalRow {
  return {
    bookingId: b.id,
    customerId: b.customer_id,
    customerName: b.customer_name ?? `#${b.customer_id.slice(0, 8)}`,
    dogNames: b.dog_names ?? [],
    booking: b,
  };
}

/**
 * Ted #2 follow-up (#905) — the arrivals households on the Bookings list:
 * one owner's pending/confirmed bookings in the list, in list order. A
 * household needs at least TWO rows; one dog has nothing to combine with,
 * and a row whose payload carries no customer id is never grouped.
 *
 * Deliberately NOT gated to the day view (contrast the check-OUT
 * `households`, which is): Journey & Solar — two of one owner's forgotten
 * visits — is the literal case Ted #27 opens with, and batching them is the
 * point. What the Outstanding list needs is not the ABSENCE of this card
 * but that the batch send each dog's own corrected arrival, which
 * `handleHouseholdCheckIn` does. The check-out card stays day-only for a
 * different reason: it puts the family on ONE invoice, and an invoice
 * cannot carry two dates.
 */
export function arrivalHouseholds(
  bookings: readonly AdminBooking[],
): Map<string, ArrivalHousehold> {
  const rows = bookings
    // #984 09-16 re-review — a SERVICE LEG is never a household member. It
    // carries the parent's customer AND the parent's dogs, so a single dog
    // with one added service produced a two-row "household" — a family card
    // for a family of one — and made the leg a checkbox in the sibling
    // prompt, a "sibling" that is the same dog. The parent's check-in
    // cascades to it either way, so nothing is lost by leaving it out; the
    // Front Desk card has always filtered legs this way.
    .filter((b) => b.isLeg !== true)
    .filter((b) => isArrivalStatus(b.status))
    .map(toArrivalRow);
  const out = new Map<string, ArrivalHousehold>();
  for (const segment of segmentByHousehold(rows)) {
    if (segment.customerId === null || segment.rows.length < 2) continue;
    out.set(segment.customerId, {
      customerId: segment.customerId,
      customerName: segment.customerName,
      rows: segment.rows,
    });
  }
  return out;
}

/** "Biscuit & Marmalade — Wren Halloway" (dogs first, owner second). */
export function arrivalHouseholdLabel(h: ArrivalHousehold): string {
  const dogs = joinNames(uniqueDogNames(h.rows.map((r) => r.booking)));
  return dogs ? `${dogs} — ${h.customerName}` : h.customerName;
}

/**
 * How many DOGS these bookings put through the door, or null when the
 * payload cannot say (a row that carries no dog names).
 *
 * Review fix (#905/#987): the card and the prompt counted BOOKINGS while
 * the copy said "dogs". A booking carries `dog_names` as an ARRAY — a
 * family with one 2-dog boarding row plus a daycare row read "Check in all
 * 2 family dogs" while three dogs went in. Principle 5: never state a
 * number the data does not support.
 */
export function arrivalDogCount(rows: readonly AdminBooking[]): number | null {
  if (rows.some((b) => (b.dog_names?.length ?? 0) === 0)) return null;
  return uniqueDogNames(rows).length;
}

/** "3 dogs" when the rows name their dogs; "2 visits" when they cannot. */
export function arrivalCountLabel(rows: readonly AdminBooking[]): string {
  const dogs = arrivalDogCount(rows);
  if (dogs === null) return `${rows.length} ${rows.length === 1 ? 'visit' : 'visits'}`;
  return `${dogs} ${dogs === 1 ? 'dog' : 'dogs'}`;
}

/**
 * Ted #26 (#987) — the prompt on a SINGLE check-in when this family has
 * other dogs booked the same day. Every sibling is listed with its service
 * and times and its own checkbox so an odd reservation is never swept in:
 * "reduce duplicate work, but never remove staff control." With no siblings
 * there is no prompt — check-in stays one tap.
 *
 * Ted #32c (#1160) — the dialog itself is the shared `FamilyCheckInDialog`
 * (Front Desk + dashboard use it too), and the siblings now start UNTICKED:
 * only the tapped dog goes in unless the desk ticks another ("it should not
 * automatically check them all in"). Which boxes are ticked is the dialog's
 * own state; this holds only who the prompt is about.
 */
interface SiblingPromptState {
  readonly booking: AdminBooking;
  readonly siblings: readonly AdminBooking[];
}

type DisplayEntry =
  | { readonly kind: 'row'; readonly booking: AdminBooking }
  | { readonly kind: 'household'; readonly household: HouseholdGroup }
  | { readonly kind: 'arrival-household'; readonly household: ArrivalHousehold };

interface SettleDialogState {
  readonly booking: AdminBooking;
  readonly bookingLabel: string;
  readonly invoice: AdminOpenInvoice;
  readonly idempotencyKey: string;
  /** Ted #10 (#913) — set when the dialog collects a whole household. */
  readonly household?: { readonly dogNames: readonly string[]; readonly visits: number };
}

export function Bookings({ config }: BookingsProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  // Render-scope token snapshot for child action controls (e.g. the grooming
  // groomer picker). Handlers still read a fresh token via readSession() at
  // call time; this is only for components whose action is user-triggered.
  const sessionToken = readSession().token ?? '';
  // #251 should-fix 5 — the dashboard's departure rows deep-link here
  // (?focus=<bookingId>) instead of POSTing check-out directly, so every
  // check-out flows through THIS page's money-path orchestration. The
  // focused row is highlighted and scrolled into view.
  const [searchParams] = useSearchParams();
  const focusBookingId = searchParams.get('focus');
  const [date, setDate] = useState<string>(() => todayIsoDate());
  const dateFilterRef = useRef<HTMLInputElement>(null);
  // Ted #27 part 2 (#988) — Day | Outstanding. Deep-linkable (?view=
  // outstanding) so the dashboard / front desk can send the desk straight
  // to the forgotten visits without teaching a navigation path.
  const [view, setView] = useState<BookingsView>(() =>
    searchParams.get('view') === 'outstanding' ? 'outstanding' : 'day',
  );
  const [outstanding, setOutstanding] = useState<OutstandingMeta>({
    kind: 'loading',
  });
  const [serviceTypeId, setServiceTypeId] = useState<string>('');
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeSummary[]>([]);
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [actionError, setActionError] = useState<string | null>(null);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [tierConfirm, setTierConfirm] = useState<TierConfirmDialogState | null>(
    null,
  );
  // Sawyer 13 household-checkout-grooming (C3) — the household tier steps,
  // and the customer's requested add-ons for whichever groom's tier dialog is
  // open (single row or step). A non-grooming visit never calls the read.
  const [householdTierSteps, setHouseholdTierSteps] = useState<HouseholdTierSteps | null>(null);
  const tierDialogBookingId =
    householdTierSteps?.queue[householdTierSteps.index]?.id ?? tierConfirm?.booking.id ?? null;
  const requestedAddOns = useRequestedAddOns(apiUrl, readSession().token, tierDialogBookingId);
  /** Single-flight per booking: a double press never applies the same request twice. */
  const addOnApplyInFlight = useRef(new Map<string, Promise<void>>());
  // #314 — irreversible-cancel confirm gate. Holds the booking pending a
  // user-confirmed cancel; null when no confirm is open.
  // Ted #16 — undo-check-in confirm; Ted #6 — per-booking corrected
  // check-out instant (datetime-local value, empty = "now").
  const [undoConfirm, setUndoConfirm] = useState<{
    booking: AdminBooking;
    // Ted #22 (#983) — the same confirm dialog serves both undos; `kind`
    // picks the copy and the call. A second near-identical dialog would be
    // two places to keep the wording honest.
    kind: 'check-in' | 'check-out';
  } | null>(null);
  // Ted #23 (#984) — "Add a service" on the row. Until this shipped there was
  // NO desk path to put an add-on on an existing visit, which is why Milo's
  // nail trim existed only in Gingr.
  const [addServiceFor, setAddServiceFor] = useState<AdminBooking | null>(null);
  const undoDismissRef = useRef<HTMLButtonElement>(null);
  // Ted #26 (#987) — the "this dog only / all N family dogs" prompt.
  const [siblingPrompt, setSiblingPrompt] = useState<SiblingPromptState | null>(null);
  // Ted #26 / Item 24 — the household card's "Check in all" opens the same
  // family picker Front Desk and the dashboard use, everyone ticked, instead
  // of posting on the tap. Holds the household the picker is about.
  const [householdPicker, setHouseholdPicker] = useState<ArrivalHousehold | null>(null);
  // Customer ids whose "Check in all" batch is in flight — drives the
  // button's busy label and aria-busy.
  const [householdCheckInBusy, setHouseholdCheckInBusy] = useState<ReadonlySet<string>>(
    new Set<string>(),
  );
  // The good half of a household check-in: status tone, never error chrome.
  const [checkInNotice, setCheckInNotice] = useState<string | null>(null);
  // Ted #28 (#1050) — the arrival-verify step, shared with Front Desk and
  // the dashboard: the server refuses a transported dog's check-in until a
  // human confirms it is here; this asks, one dog at a time, and re-runs.
  const { ask: askArrival, dialog: arrivalDialog } = useArrivalVerification();
  // Lane LC — the three steps above, one queue each.
  const { ask: askInterrupt, dialog: interruptDialog } = useCheckInInterrupt();
  const { ask: askCheckout, dialog: checkoutInterruptDialog } = useCheckoutInterrupt();
  const inHeat = useInHeatAction(apiUrl, () => readSession().token);
  // Ted Item 34 — "Print the run card?" the moment a check-in succeeds. A
  // backdated check-in (#988, a forgotten visit) gets no prompt: the card is
  // for a dog on site now, and the server finds no card for a past day.
  // Sawyer #1296 — filled once the owner's-notes prompt exists (below the
  // confirm flow, whose dialog it waits behind).
  const ownerNotesOfferRef = useRef<OwnerNotesPrompt['offer']>(() => undefined);
  const runCardPrompt = useRunCardPrompt({
    apiUrl,
    token: readSession().token,
    enabled: config.features?.runCardPromptOnCheckIn !== false,
  });
  // Ted #22 — the reason the server requires; it lands on the voided invoice
  // and on the audit row, so the next person knows why.
  const [undoReason, setUndoReason] = useState('');
  const [checkoutTimeOverride, setCheckoutTimeOverride] = useState<Record<string, string>>({});
  // Ted #27 part 2 (#988) — the corrected ARRIVAL instant for a forgotten
  // pending/confirmed visit. Without it the check-in stamps TODAY and the
  // original-date check-out is then refused 422 `before_check_in`, so the
  // charge could only land on the wrong day. Empty = "now" (the day list).
  const [checkinTimeOverride, setCheckinTimeOverride] = useState<Record<string, string>>({});
  // Ted #5 (#908) — the desk's per-booking package choice (apply? which?).
  // Absent = the default (apply the first active package). Read through a
  // ref at checkout time so the money-path callbacks stay stable.
  const [packageChoiceByBooking, setPackageChoiceByBooking] = useState<
    Record<string, PackageChoice>
  >({});
  const packageChoiceByBookingRef = useRef(packageChoiceByBooking);
  packageChoiceByBookingRef.current = packageChoiceByBooking;
  // Ted #11 (#914) — the server's late-pickup proposal per boarding row, and
  // the desk's disposition. Both read through refs at checkout time so the
  // money-path callbacks stay stable (the #908 pattern).
  const [latePickupByBooking, setLatePickupByBooking] = useState<
    Record<string, LatePickupState>
  >({});
  const latePickupByBookingRef = useRef(latePickupByBooking);
  latePickupByBookingRef.current = latePickupByBooking;
  const [latePickupChoiceByBooking, setLatePickupChoiceByBooking] = useState<
    Record<string, LatePickupChoice>
  >({});
  const latePickupChoiceByBookingRef = useRef(latePickupChoiceByBooking);
  latePickupChoiceByBookingRef.current = latePickupChoiceByBooking;
  /**
   * "Is it past 11:00 yet" is a question whose answer changes on its own. A
   * screen opened at 10:55 and used at 11:05 would otherwise keep showing
   * "not late" and the charge would be silently missed — the fail-OPEN
   * direction on a money rule. Re-asking once a minute is a handful of reads
   * over the boarders going home today.
   */
  const [latePickupTick, setLatePickupTick] = useState(0);
  /**
   * Ted #26 (#987) / #923 — the approval queue. OFF is the day view this page
   * has always been; ON replaces the single-date query with every PENDING
   * reservation in the lookup window, oldest first, which is the only place
   * a reservation nobody actioned (HTHD has five, all past-dated) can be
   * found without knowing its date first.
   */
  const [pendingQueue, setPendingQueue] = useState(false);
  // Ted #32c / E3.3 (#1158) — FIND A VISIT: a name search and "Recently checked
  // out". `searchInput` is what is typed; `searchQuery` is it settled (so a
  // window is read when the desk pauses, not on every keystroke).
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [recentOut, setRecentOut] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput.trim()), 250);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const searchActive = searchQuery.length >= FIND_MIN_CHARS;
  const finding = view === 'day' && !pendingQueue && (searchActive || recentOut);
  // Ted #25 (#986) · Ted #32c (#1158) — Cancel, Edit and No-show on a row, as
  // the shared hook the Front Desk and dashboard Expected rows use. Cancel
  // keeps THIS page's optimistic flip + rollback (`handleSimpleAction`); Edit
  // and No-show refresh the day through `load`.
  const reservation = useReservationActions({
    apiUrl,
    onChanged: () => void load(),
    serviceTypes,
    // An existing e2e contract (edit-reservation.spec.ts) reads this id.
    bannerTestId: 'edit-reservation-notice',
    performCancel: (target) => {
      if (target.booking) void handleSimpleAction(target.booking, 'cancel');
    },
  });
  // Ted #30 (lane LR) — the series whose view is open, or null.
  const [seriesView, setSeriesView] = useState<string | null>(null);
  const [runCardsView, setRunCardsView] = useState<{
    readonly cards: ReadonlyArray<RunCardData>;
    readonly dateLabel: string;
    /** Ted #46 — loaded as today's version, so printing re-records the visit. */
    readonly live?: boolean;
  } | null>(null);
  const [runCardsLoading, setRunCardsLoading] = useState(false);
  const [runCardsError, setRunCardsError] = useState<string | null>(null);
  // Issue #26 — post-checkout package summary. Surfaces "3 days → 2
  // days" + a "Send courtesy reminder" CTA when the new balance is ≤1.
  const [packageSummary, setPackageSummary] = useState<{
    readonly booking: AdminBooking;
    readonly pre: CustomerPackage;
    readonly post: CustomerPackage;
  } | null>(null);
  const packageSummaryDialogRef = useRef<HTMLDivElement>(null);
  const packageSummaryCloseRef = useRef<HTMLButtonElement>(null);
  const packageSummaryRestoreFocusRef = useRef<HTMLElement | null>(null);
  const [reminderSent, setReminderSent] = useState(false);
  // Issue #140 — package redeem can fail (e.g. 409 insufficient). The
  // checkout already succeeded, so this surfaces as a non-blocking notice.
  const [packageNotice, setPackageNotice] = useState<string | null>(null);
  // Issue #251 — the settle-now payment dialog (card-on-file / desk
  // payment / explicit settle-later). Opens only AFTER the open-invoice
  // money record exists, so dismissing it can never lose the booking from
  // the money funnel.
  const [settleDialog, setSettleDialog] = useState<SettleDialogState | null>(
    null,
  );
  // Issue #251 — BLOCKING failure surface: the booking is checked out but
  // the open-invoice money record could NOT be written. Never silent.
  const [moneyRecordFailure, setMoneyRecordFailure] =
    useState<MoneyRecordFailureState | null>(null);
  // Ted #10 (#913) — money-record failures from a household checkout wait
  // here and block ONE at a time, after the family's settle dialog closes
  // (never a modal on a modal). Each Retry re-POSTs that dog's exact record.
  const [queuedMoneyFailures, setQueuedMoneyFailures] = useState<
    readonly MoneyRecordFailureState[]
  >([]);
  // Issue #251 — non-blocking confirmations from the money path (invoice
  // queued for end-of-day, covered-by-package settle hiccup, …).
  const [settleNotice, setSettleNotice] = useState<string | null>(null);
  // #251 should-fix 7 — the package-redeem POST returned an INDETERMINATE
  // outcome (timeout/5xx): staff must verify before any money is
  // collected; the money path halted with NO invoice created.
  const [redeemIndeterminate, setRedeemIndeterminate] = useState<
    string | null
  >(null);
  // Issue #140 — package lookups are now async. We pre-load the active
  // package for each visible booking's (customer, serviceKind) so the
  // render-time "Apply Package" preview affordance (line ~679, AC2) stays
  // a synchronous map lookup instead of a render-time fetch. Keyed by
  // `${customerId}::${serviceKind}`. (Choice (a) from the brief — keeps
  // the preview AC behavior; lowest risk vs. removing the affordance.)
  // Ted #5 (#908): ALL active packages per key (oldest purchase first), so
  // the desk can pick one when the customer holds several.
  const [activePackageByKey, setActivePackageByKey] = useState<
    Map<string, CustomerPackage[]>
  >(new Map());
  // Latest active-package map, readable from callbacks without adding it to
  // their dep arrays (avoids recreating handlers on every balance change).
  const activePackageByKeyRef = useRef(activePackageByKey);
  activePackageByKeyRef.current = activePackageByKey;

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const { token } = readSession();
    if (!token) {
      setState({
        kind: 'error',
        message: humanizeApiError('unauthorized'),
        forbidden: false,
      });
      return;
    }
    try {
      if (view === 'outstanding') {
        // Ted #27 part 2 (#988) — one call fills BOTH the list and the tab
        // badge, so the count and the rows can never describe two different
        // answers (Principle 1). The server owns "past" (facility day) and
        // the ordering (oldest first) — the client never re-sorts it.
        const res = await fetchOutstandingBookings(apiUrl, token, {
          limit: OUTSTANDING_PAGE_SIZE,
        });
        setOutstanding({
          kind: 'ready',
          total: res.total,
          before: res.before,
          timezone: res.timezone,
        });
        // Ted #6 / #909 — every row arrives with its own service day already
        // in the corrected-check-out field, so completing a three-week-old
        // visit charges THAT day and not today. Never overwrites a value the
        // desk has already typed.
        setCheckoutTimeOverride((prev) =>
          seedOutstandingCheckoutTimes(prev, res.bookings),
        );
        // …and the ARRIVAL half, for the pending/confirmed rows (81 of
        // HTHD's 111). Checking one in at TODAY is what made the
        // original-date check-out impossible before the 09-16 fix.
        setCheckinTimeOverride((prev) =>
          seedOutstandingCheckinTimes(prev, res.bookings),
        );
        setState({ kind: 'ready', bookings: res.bookings });
        return;
      }
      // Ted #26 (#923/#1007) — the approval queue spans dates and filters to
      // `pending`; the day view is one day. Neither reaches the Outstanding
      // branch above, which answers a different question entirely.
      // Ted #32c / E3.3 — a find reads a window of dates (and, for "Recently
      // checked out", one status); the name is matched on the client over it.
      const range = pendingQueue
        ? { ...pendingWindow(), status: 'pending' }
        : finding
          ? findWindow(recentOut)
          : { startDate: date, endDate: date };
      const bookings = await fetchAdminSchedule(apiUrl, token, {
        ...range,
        serviceTypeId: serviceTypeId || undefined,
      });
      setState({ kind: 'ready', bookings });
    } catch (err) {
      // Never surface a raw server code (e.g. the bare "forbidden" a role the
      // server denies used to see in a red box). A 403 is a permission wall,
      // not a transient failure — show the friendly access message and a way
      // OUT (Retry can't fix a permission), not a Retry button. Every other
      // failure keeps the retryable error state.
      const forbidden = err instanceof ApiError && err.status === 403;
      const message = forbidden
        ? humanizeApiError('forbidden')
        : humanizeApiError(err instanceof Error ? err.message : null);
      setState({ kind: 'error', message, forbidden });
      if (view === 'outstanding') setOutstanding({ kind: 'error', message });
    }
  }, [apiUrl, date, serviceTypeId, pendingQueue, view, finding, recentOut]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * 09-16 re-review, the major — a corrected date belongs to the LIST it was
   * seeded or typed on, and to nothing else.
   *
   * `seedOutstandingCheck{in,out}Times` fill these maps for every row on the
   * Outstanding list, but the fields that DISPLAY them are drawn only there
   * (the arrival always, the grooming check-out too). Left alone, the maps
   * survived a tab switch and a date change while the controls did not — so
   * completing the same visit from the day list posted a backdated
   * `checked_in_at` (and, on a grooming row, a backdated `checked_out_at`)
   * with nothing on screen to see or correct, and a 422 was a dead end. A
   * money-dated write the desk cannot see is the Counter Test failure this
   * PR already fixed once, pointed at a different surface.
   *
   * Dropping both maps whenever the QUESTION changes — which view, which
   * day, which service — removes the stale value at the source; the submit
   * paths additionally ask `correctedArrivalVisible` /
   * `correctedCheckoutVisible`, the same call the render makes, so a field
   * that is not drawn cannot be sent even if a value somehow survives.
   * Nothing is lost: the desk's edits live exactly as long as the list they
   * were made on, and `load()` re-seeds the proposals on the way back in.
   */
  useEffect(() => {
    setCheckoutTimeOverride({});
    setCheckinTimeOverride({});
  }, [view, date, serviceTypeId]);

  /**
   * The tab badge on the DAY view. Same endpoint, `limit: 1` — we only need
   * the server's `total`. Deliberately NOT derived from the day's rows: the
   * whole point of the tab is the visits that are not on today's list.
   * A failure lands in `{ kind: 'error' }`, which the badge renders as an
   * unknown count — never as zero (Principle 5).
   */
  const refreshOutstandingMeta = useCallback(async () => {
    const { token } = readSession();
    if (!token) return;
    try {
      const res = await fetchOutstandingBookings(apiUrl, token, { limit: 1 });
      setOutstanding({
        kind: 'ready',
        total: res.total,
        before: res.before,
        timezone: res.timezone,
      });
    } catch (err) {
      setOutstanding({
        kind: 'error',
        message:
          err instanceof ApiError && err.status === 403
            ? humanizeApiError('forbidden')
            : humanizeApiError(err instanceof Error ? err.message : null),
      });
    }
  }, [apiUrl]);

  useEffect(() => {
    // The outstanding view's own load already carries a fresh count.
    if (view === 'outstanding') return;
    void refreshOutstandingMeta();
  }, [view, refreshOutstandingMeta]);

  // Post-void booking coherence (money-path audit P1). Best-effort,
  // NON-BLOCKING side-read of the desk transactions feed so a booking whose
  // only payment was voided/refunded can surface a "collect again or cancel"
  // affordance instead of silently reading paid/confirmed. Isolated from the
  // schedule fetch: any failure just leaves the map empty (no banners, the
  // list renders exactly as before). Re-runs whenever the schedule query
  // changes (load identity) and on remount (returning from the Payments
  // page after a void).
  const [reversedBookings, setReversedBookings] = useState<
    Map<string, 'voided' | 'refunded'>
  >(new Map());
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await adminPaymentsApi.listTransactions(apiUrl, {
        limit: 100,
      });
      if (cancelled) return;
      if (res.error || !res.data) {
        setReversedBookings(new Map());
        return;
      }
      setReversedBookings(
        deriveReversedBookings(res.data.transactions ?? []),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, load]);

  // Scroll the deep-linked booking row into view once the list is ready.
  useEffect(() => {
    if (!focusBookingId || state.kind !== 'ready') return;
    const el = document.getElementById(`booking-row-${focusBookingId}`);
    el?.scrollIntoView({ block: 'center' });
  }, [focusBookingId, state.kind]);

  useEffect(() => {
    if (undoConfirm) undoDismissRef.current?.focus();
  }, [undoConfirm]);

  useEffect(() => {
    if (!packageSummary) return undefined;
    packageSummaryRestoreFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    packageSummaryCloseRef.current?.focus();
    return () => {
      const restoreTarget = packageSummaryRestoreFocusRef.current;
      if (restoreTarget?.isConnected && restoreTarget !== document.body) {
        restoreTarget.focus();
      } else {
        dateFilterRef.current?.focus();
      }
    };
  }, [packageSummary]);

  const closePackageSummary = useCallback((): void => {
    setPackageSummary(null);
  }, []);

  const handlePackageSummaryKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closePackageSummary();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable =
        packageSummaryDialogRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
        );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [closePackageSummary],
  );

  useEffect(() => {
    const { token } = readSession();
    if (!token) return;
    let cancelled = false;
    void fetchServiceTypes(apiUrl, token)
      .then((list) => {
        if (!cancelled) setServiceTypes(list);
      })
      .catch(() => {
        // Non-fatal — the filter just stays empty.
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  const serviceTypeById = useMemo(() => {
    const map = new Map<string, ServiceTypeSummary>();
    for (const s of serviceTypes) map.set(s.id, s);
    return map;
  }, [serviceTypes]);

  // Ted #26 (#987) — the confirm flow. The hook owns the "does this dog have
  // other pending reservations?" lookup and both outcomes (one tap vs the
  // review dialog); this page only supplies the service NAME (never the
  // engine key — DESIGN-PHILOSOPHY: no raw identifiers) and the refresh.
  const serviceLabelForBooking = useCallback(
    (b: AdminBooking): string =>
      serviceTypeById.get(b.service_type_id)?.display_name ?? 'Reservation',
    [serviceTypeById],
  );
  const confirmFlow = useConfirmPending({
    apiUrl,
    serviceLabelForBooking,
    onApplied: () => void load(),
    // Sawyer #1296 — "Add this to the owner's notes?" after an accept.
    onConfirmed: (items) =>
      ownerNotesOfferRef.current(
        items.map((i) =>
          factsFromPending(
            i,
            (state.kind === 'ready' ? state.bookings.find((b) => b.id === i.bookingId)?.customer_name : null) ?? null,
          ),
        ),
      ),
  });
  // Sawyer #1296 — the owner's-notes question waits behind every other
  // dialog on this page (never modal-on-modal).
  const ownerNotes = useOwnerNotesPrompt({
    apiUrl,
    onAdded: () => void load(),
    blocked:
      runCardPrompt.active ||
      siblingPrompt !== null ||
      arrivalDialog !== null ||
      interruptDialog !== null ||
      confirmFlow.dialogProps !== null,
  });
  ownerNotesOfferRef.current = ownerNotes.offer;
  const beginConfirm = useCallback(
    (b: AdminBooking): void => {
      void confirmFlow.begin({
        bookingId: b.id,
        customerId: b.customer_id,
        customerName: b.customer_name ?? null,
        fallback: toPendingReservation(b, serviceLabelForBooking(b)),
      });
    },
    [confirmFlow, serviceLabelForBooking],
  );

  // Issue #140 — the unique (customerId, serviceKind) pairs among the visible
  // bookings that could carry a package. Recomputed when bookings/services
  // change, but its IDENTITY only matters via `packagePairsKey` below.
  const packagePairs = useMemo(() => {
    if (state.kind !== 'ready') return [] as Array<{ customerId: string; kind: PackageServiceKind }>;
    // The package preview only renders on rows that can check out, and a
    // PENDING row cannot. Skipping the pre-load in queue mode is therefore
    // free — and it is what keeps a 100-row queue from firing ~100 package
    // lookups the moment it opens.
    if (pendingQueue) return [] as Array<{ customerId: string; kind: PackageServiceKind }>;
    const seen = new Map<string, { customerId: string; kind: PackageServiceKind }>();
    // Ted #32c / E3.3 — a find reads a window of dates (a couple of months of
    // visits). Only the rows the desk typed AND can check out could use a
    // package preview, so only those are looked up — never one lookup per
    // customer in the window.
    const rows = finding
      ? state.bookings.filter(
          (b) =>
            nextActions(b.status, b.isLeg === true).includes('check-out') &&
            (!searchActive || bookingMatchesSearch(b, searchQuery)),
        )
      : state.bookings;
    for (const b of rows) {
      const kind = packageKindForService(serviceTypeById.get(b.service_type_id)?.name);
      if (!kind) continue;
      seen.set(packageKey(b.customer_id, kind), { customerId: b.customer_id, kind });
    }
    return Array.from(seen.values());
  }, [state, serviceTypeById, pendingQueue, finding, searchActive, searchQuery]);

  // A stable signature of the pair SET. A booking status flip (check-in/out/
  // cancel) leaves this unchanged, so the pre-load effect does NOT re-fetch on
  // every mutation (avoids an N-per-mutation fetch storm) and can't race-
  // overwrite the post-redeem balance set by `redeemPackageForCheckout`.
  const packagePairsKey = useMemo(
    () => packagePairs.map((p) => packageKey(p.customerId, p.kind)).sort().join('|'),
    [packagePairs],
  );
  const packagePairsRef = useRef(packagePairs);
  packagePairsRef.current = packagePairs;

  // Pre-load active packages for the visible bookings so the render-time
  // preview affordance can stay synchronous. Fetches once per unique
  // (customerId, serviceKind) pair; on error a key is simply absent and the
  // preview just doesn't render (graceful). Keyed on the stable pair set.
  useEffect(() => {
    const { token } = readSession();
    if (!token) return;
    const pairs = packagePairsRef.current;
    if (pairs.length === 0) {
      setActivePackageByKey(new Map());
      return;
    }
    let cancelled = false;
    void Promise.all(
      pairs.map(async ({ customerId, kind }) => {
        const res = await adminPaymentsApi.listPackages(apiUrl, customerId, kind);
        const active = activePackagesOf(res.data?.packages);
        return { key: packageKey(customerId, kind), active };
      }),
    ).then((entries) => {
      if (cancelled) return;
      const next = new Map<string, CustomerPackage[]>();
      for (const { key, active } of entries) {
        if (active.length > 0) next.set(key, active);
      }
      setActivePackageByKey(next);
    });
    return () => {
      cancelled = true;
    };
    // packagePairsRef.current is read at run time; re-run only when the pair
    // SET (packagePairsKey) or apiUrl changes — not on every booking mutation.
  }, [apiUrl, packagePairsKey]);

  // ── Ted #11 (#914): the boarding checkout cutoff ────────────────────────
  // Only rows that can actually be checked out AND are boarding can carry a
  // late pickup, so only those are asked about. Everything else — the
  // owner's times, the facility's timezone, whether today is the stay's last
  // day, what a daycare day costs — is the SERVER's answer, never derived
  // here: a browser clock is the user's, not the facility's.
  const latePickupCandidates = useMemo(() => {
    if (state.kind !== 'ready') return [] as string[];
    return state.bookings
      .filter(
        (b) =>
          nextActions(b.status).includes('check-out') &&
          isBoardingServiceName(serviceTypeById.get(b.service_type_id)?.name),
      )
      .map((b) => b.id);
  }, [state, serviceTypeById]);
  const latePickupCandidatesKey = useMemo(
    () => [...latePickupCandidates].sort().join('|'),
    [latePickupCandidates],
  );
  const latePickupCandidatesRef = useRef(latePickupCandidates);
  latePickupCandidatesRef.current = latePickupCandidates;

  useEffect(() => {
    if (latePickupCandidatesRef.current.length === 0) return;
    const id = window.setInterval(() => setLatePickupTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, [latePickupCandidatesKey]);

  useEffect(() => {
    const ids = latePickupCandidatesRef.current;
    if (ids.length === 0) return;
    const { token } = readSession();
    if (!token) return;
    let cancelled = false;
    // Mark only rows we have never answered for as loading; a refresh must
    // not flash a skeleton over a control the desk is mid-way through using.
    setLatePickupByBooking((prev) => {
      const next = { ...prev };
      for (const id of ids) if (!next[id]) next[id] = { status: 'loading' };
      return next;
    });
    void Promise.all(
      ids.map(async (bookingId) => {
        const res = await adminPaymentsApi.latePickup(apiUrl, bookingId);
        return { bookingId, res };
      }),
    ).then((entries) => {
      if (cancelled) return;
      // MERGE, never replace. Checking a dog out flips its status, which
      // takes the row out of the candidate set — and the money leg runs
      // AFTER that flip. Rebuilding the map from the current candidates
      // would therefore delete the proposal the checkout is about to act
      // on, and the charge would vanish between the click and the invoice.
      // Entries are keyed by booking id, so keeping a departed row costs a
      // few bytes and buys the disposition surviving its own checkout.
      setLatePickupByBooking((prev) => {
        const next = { ...prev };
        for (const { bookingId, res } of entries) {
          next[bookingId] = res.data?.latePickup
            ? { status: 'ready', proposal: res.data.latePickup }
            : { status: 'error', message: res.error ?? 'unknown error' };
        }
        return next;
      });
      // Seed the desk's opening choice for any row that has just become
      // late, without disturbing a choice already made on another row.
      setLatePickupChoiceByBooking((prev) => {
        const next = { ...prev };
        for (const { bookingId, res } of entries) {
          const proposal = res.data?.latePickup;
          if (proposal?.applicable && proposal.late && !next[bookingId]) {
            next[bookingId] = defaultLatePickupChoice(proposal);
          }
        }
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, latePickupCandidatesKey, latePickupTick]);

  /** Re-ask for ONE row (the error state's Retry). */
  const reloadLatePickup = useCallback(
    async (bookingId: string) => {
      setLatePickupByBooking((prev) => ({ ...prev, [bookingId]: { status: 'loading' } }));
      const res = await adminPaymentsApi.latePickup(apiUrl, bookingId);
      const proposal = res.data?.latePickup;
      setLatePickupByBooking((prev) => ({
        ...prev,
        [bookingId]: proposal
          ? { status: 'ready', proposal }
          : { status: 'error', message: res.error ?? 'unknown error' },
      }));
      if (proposal?.applicable && proposal.late) {
        setLatePickupChoiceByBooking((prev) =>
          prev[bookingId] ? prev : { ...prev, [bookingId]: defaultLatePickupChoice(proposal) },
        );
      }
    },
    [apiUrl],
  );

  // #905/#987 review fix: this used to REPLACE the row with the action
  // response. Every single-booking action response omits the schedule's
  // dog-first enrichment, so the replacement silently blanked the dog
  // photos, the "Dog — Owner" line and the balance badge on the row the
  // desk had just acted on. `mergeActionResponse` keeps what the list knows.
  // That merge matters MORE on the Outstanding list, whose rows carry the
  // original date, the Gingr id and the overdue age — none of which a
  // check-in/check-out response returns.
  const applyBookingUpdate = useCallback(
    (next: AdminBooking) => {
      setState((curr) => {
        if (curr.kind !== 'ready') return curr;
        return {
          kind: 'ready',
          bookings: curr.bookings.map((b) =>
            b.id === next.id ? mergeActionResponse(b, next) : b,
          ),
        };
      });
      // Ted #27 (#988) — a completed visit leaves the outstanding set, so
      // the tab badge is re-read from the server rather than decremented
      // here. The row itself STAYS on screen showing its new status: the
      // desk gets to see that the thing it just did landed, and the badge
      // still tells the truth about what is left.
      void refreshOutstandingMeta();
    },
    [refreshOutstandingMeta],
  );

  const rollbackTo = useCallback((previous: AdminBooking[]) => {
    setState({ kind: 'ready', bookings: previous });
  }, []);

  // Issue #140/#251/#252 — redeem one day from the customer's active
  // package at check-out. Runs for BOTH tracks (settle-now AND
  // settle-later) and ONLY at check-out — the settle leg never redeems —
  // so a redeemed booking decrements exactly once (#252). The check-out
  // has already succeeded by the time this runs, so failures are surfaced
  // as a non-blocking notice (NOT best-effort-silent — this consumes a
  // paid balance) and the caller falls back to the cash money path. On
  // success the post-checkout summary (pre→post) renders and the cache is
  // refreshed; the caller writes a $0 covered invoice.
  const redeemPackageForCheckout = useCallback(
    async (
      booking: AdminBooking,
      kind: PackageServiceKind,
      /**
       * #1157 E2.4 — package days this visit draws: one per dog on the
       * booking (`packageDaysForVisit`). Was always 1, so a booking carrying
       * two dogs drew one day for two visits.
       */
      days: number,
    ): Promise<PackageRedemptionOutcome> => {
      const cacheKey = packageKey(booking.customer_id, kind);
      // Ted #5 (#908) — the desk's choice on the row: apply (default) or
      // charge the visit and keep the package; and which package when the
      // customer holds several. Declining never touches the server.
      const choice =
        packageChoiceByBookingRef.current[booking.id] ?? DEFAULT_PACKAGE_CHOICE;
      if (!choice.apply) return { applied: false, declined: true };
      // Prefer the pre-loaded active packages (already fetched for the row);
      // only fall back to a direct read on a cache miss (e.g. preload pending).
      let candidates = activePackageByKeyRef.current.get(cacheKey) ?? null;
      if (!candidates) {
        const listed = await adminPaymentsApi.listPackages(
          apiUrl,
          booking.customer_id,
          kind,
        );
        if (listed.error || !listed.data) {
          setPackageNotice(
            `Checked out, but could not read the package balance: ${listed.error ?? 'unknown error'}`,
          );
          return { applied: false };
        }
        candidates = activePackagesOf(listed.data.packages);
      }
      const pre = resolveChosenPackage(candidates, choice);
      if (!pre) return { applied: false }; // No active package — nothing to redeem.
      // #1157 E2.4 — a package short of the visit's days covers none of it:
      // the server refuses a partial draw (409), so say why here instead and
      // charge the visit in full. The package keeps its days.
      if (pre.remainingDays < days) {
        setPackageNotice(
          `Checked out. The package has ${pre.remainingDays} day${pre.remainingDays === 1 ? '' : 's'} left and this visit needs ${days} (one per dog), so the visit is charged in full and the package keeps its days.`,
        );
        return { applied: false };
      }

      // The booking rides the redeem so the server verifies ownership and
      // records which visit used the day (Ted #1's package history).
      const redeemed = await adminPaymentsApi.redeemPackage(apiUrl, pre.id, days, booking.id);
      if (redeemed.error || !redeemed.data) {
        // #251 should-fix 7 — split DEFINITIVE failures from
        // INDETERMINATE ones. A timeout/5xx/network drop means the
        // server MAY have burned the day: proceeding to a full-price
        // invoice could charge a customer who already paid with a
        // package day. Definitive failures (e.g. 409 insufficient) are
        // safe to fall back to the cash path.
        if (redeemed.isRetryable) {
          return {
            applied: 'indeterminate',
            error: redeemed.error ?? 'no response from the server',
          };
        }
        setPackageNotice(
          `Checked out, but the package day could not be applied: ${redeemed.error ?? 'unknown error'}`,
        );
        return { applied: false };
      }
      const post = redeemed.data.package;
      setPackageSummary({ booking, pre, post });
      setReminderSent(false);
      // Keep the render-time map in sync with the new balance: replace the
      // redeemed package in its list, drop it once it hits zero.
      setActivePackageByKey((prev) => {
        const next = new Map(prev);
        const key = packageKey(booking.customer_id, kind);
        const updated = (next.get(key) ?? [pre])
          .map((p) => (p.id === post.id ? post : p))
          .filter((p) => p.remainingDays > 0);
        if (updated.length > 0) next.set(key, updated);
        else next.delete(key);
        return next;
      });
      // #1157 review — the server derives the per-dog count from the
      // booking and ignores ours; the note and the summary say what it drew.
      return { applied: true, post, days: redeemed.data.redemption?.days ?? days };
    },
    [apiUrl],
  );

  // Issue #251 — the shared MONEY RECORD leg. Every checkout (both tracks)
  // writes an open-invoice row to the server-of-record; a failure here is
  // a BLOCKING, retryable dialog — never a console.warn. Resolution:
  //   covered (package day redeemed) → $0 invoice settled immediately;
  //   settle-now                     → SettleNowDialog (charge/desk/queue);
  //   settle-later                   → invoice stays in the end-of-day queue.
  const recordMoneyLeg = useCallback(
    async (args: MoneyRecordArgs, opts?: MoneyLegOptions): Promise<MoneyLegResult> => {
      const created = await adminPaymentsApi.createOpenInvoice(apiUrl, {
        bookingId: args.booking.id,
        totalCents: args.totalCents,
        // The server schema rejects null — omit when absent.
        ...(args.notes ? { notes: args.notes } : {}),
        // Ted #11 (#914) — the late-pickup disposition, when the desk chose
        // one. No amount rides along: the daycare rate is the tenant's own
        // and the server reads it, exactly as #926's meal line does.
        ...(args.latePickup ? { latePickup: args.latePickup } : {}),
        // Ted #3 + #9 (#906 #912) — a $0 covered visit is a price change and
        // must say so: the server refuses a bare lower total.
        ...(args.covered
          ? { adjustment: { kind: 'package' as const, reason: args.coveredReason ?? 'Covered by package day' } }
          : {}),
      });
      if (created.error || !created.data?.invoice) {
        const error = created.error ?? 'unknown error';
        // Ted #10 (#913) — the household loop collects failures and blocks
        // on them one at a time AFTER the other dogs are through; the
        // single row blocks right here.
        if (!opts?.deferDialog) setMoneyRecordFailure({ ...args, error });
        return { kind: 'failed', args, error };
      }
      setMoneyRecordFailure(null);
      const invoice = created.data.invoice;

      // #926 (Ted A4) — the server may add its own line (meal tracking on a
      // boarding stay) on top of a package-covered visit, so "covered" no
      // longer implies $0: only a $0 invoice settles as the package day;
      // a residual is collected like any other amount due.
      if (args.covered && invoice.totalCents === 0) {
        // Package day already redeemed — settle the $0 invoice so the
        // end-of-day queue can't invite a second (cash) collection.
        // #812 — the tender is the package day itself.
        const settledRes = await adminPaymentsApi.settleOpenInvoice(
          apiUrl,
          invoice.id,
          { paymentMethod: 'package' },
        );
        if (settledRes.error || !settledRes.data?.invoice) {
          setSettleNotice(
            `Package day applied, but the $0 covered invoice could not be marked settled — it remains in the queue (no money is due on it): ${settledRes.error ?? 'unknown error'}`,
          );
        }
        return { kind: 'covered' };
      }

      if (args.track === 'settle-now') {
        if (opts?.deferDialog) return { kind: 'invoice', invoice };
        setSettleDialog({
          booking: args.booking,
          bookingLabel: args.bookingLabel,
          invoice,
          // One stable key per LOGICAL settle attempt (#251) — minted when
          // the dialog opens, reused for every retry within it (shared
          // helper; was the 4th copy of the minting code).
          idempotencyKey: makeIdempotencyKey('settle-now', args.booking.id),
        });
        return { kind: 'dialog-opened' };
      }
      // settle-later — queued for end-of-day; nothing more to do.
      return { kind: 'queued' };
    },
    [apiUrl],
  );

  // Issue #251/#252 — the full post-check-out money path: package
  // redemption (exactly once, both tracks) + the money record + its
  // resolution. Never throws; every failure lands in a staff-visible
  // surface (blocking dialog or notice).
  const completeMoneyPath = useCallback(
    async (
      args: {
        booking: AdminBooking;
        track: CheckoutAction;
        service: ServiceTypeSummary | null;
        totalCents: number;
        operatorNotes?: string | null;
        /**
         * #1157 E2.4 — dogs on the visit, read from the LIST row
         * (`dogCountOf`): the check-out response carries the dog-first block
         * at its empty sentinels, so `booking` here cannot be counted.
         */
        dogCount: number;
        /**
         * #1157 E2.5 — the corrected checkout instant this checkout SENT
         * (null when the server stamped "now"). A late pickup is re-read at
         * it, exactly as the server does from `checked_out_at`, so a
         * disposition is never sent for a pickup the server will call on time.
         */
        correctedCheckedOutAt?: string | null;
      },
      opts?: MoneyLegOptions,
    ): Promise<MoneyLegResult> => {
      const kind = packageKindForService(args.service?.name);
      let covered = false;
      let coveredNote: string | null = null;
      if (kind) {
        const days = packageDaysForVisit(args.service?.name, args.dogCount, stayWindowOf(args.booking));
        const redemption = await redeemPackageForCheckout(args.booking, kind, days);
        if (redemption.applied === 'indeterminate') {
          // #251 should-fix 7 — UNKNOWN outcome: the package day may or
          // may not have been used. Do NOT write a full-price invoice
          // (the customer could lose a day AND be billed). Halt the
          // money path; the visit is recoverable from the Payments
          // page's Unrecorded-visits reconciliation card.
          setRedeemIndeterminate(
            `Package redemption outcome UNKNOWN for booking ${buildBookingLabel(args.booking)} (${redemption.error}). The package day may or may not have been used — verify the customer's package balance before collecting. NO invoice was created; once verified, record this visit from the Payments page (it appears under "Unrecorded visits").`,
          );
          return { kind: 'halted' };
        }
        if (redemption.applied === true) {
          covered = true;
          const drawn = redemption.days;
          coveredNote =
            drawn === 1
              ? `Covered by ${kind} package day — ${redemption.post.remainingDays} day${redemption.post.remainingDays === 1 ? '' : 's'} left`
              : `Covered by ${kind} package days (${drawn}, one per dog) — ${redemption.post.remainingDays} day${redemption.post.remainingDays === 1 ? '' : 's'} left`;
        } else if (redemption.applied === false && redemption.declined) {
          // Ted #5 (#908) — the ledger says WHY a package holder paid full
          // rate: the desk chose to keep the day on the account.
          coveredNote = `Package on file not applied — charged at full rate (staff choice)`;
        }
      }
      const notes = [args.operatorNotes ?? null, coveredNote]
        .filter((n): n is string => Boolean(n))
        .join(' · ');
      // Ted #11 (#914) — only a row the SERVER called late can carry a
      // disposition, and only when the desk's choice is complete (a waiver
      // needs its note). Anything else sends nothing and charges nothing.
      const latePickupState = latePickupByBookingRef.current[args.booking.id];
      const proposal = applyCorrectedCheckout(
        latePickupState?.status === 'ready' ? latePickupState.proposal : null,
        args.correctedCheckedOutAt ?? null,
      ).proposal;
      const latePickup =
        proposal?.applicable && proposal.late
          ? latePickupDisposition(
              latePickupChoiceByBookingRef.current[args.booking.id] ??
                defaultLatePickupChoice(proposal),
              proposal,
            )
          : null;
      return recordMoneyLeg(
        {
          booking: args.booking,
          bookingLabel: buildBookingLabel(args.booking),
          track: args.track,
          totalCents: covered ? 0 : args.totalCents,
          notes: notes || null,
          covered,
          coveredReason: coveredNote,
          ...(latePickup ? { latePickup } : {}),
        },
        opts,
      );
    },
    [redeemPackageForCheckout, recordMoneyLeg],
  );

  // Direct check-in / cancel actions (no two-track branching).
  const handleSimpleAction = async (
    booking: AdminBooking,
    action: 'check-in' | 'cancel' | 'undo-check-in' | 'undo-check-out',
    reason?: string,
    /**
     * Ted #28 (#1050) — the desk's answer to "is the dog here?" on a re-run.
     * Lane LC — plus the "Before you check in" step's decisions: `interrupted`
     * means the step ran (or was not needed); `heatConfirmed` rides to the server.
     */
    opts?: { arrivalVerified?: boolean; heatConfirmed?: boolean; interrupted?: boolean },
  ): Promise<void> => {
    const { token } = readSession();
    if (!token || state.kind !== 'ready') return;
    // Lane LC — "Before you check in", from the row's own flags, BEFORE the
    // POST and before the optimistic flip. The 409 fallback (arrivalVerified)
    // and the step's own re-run skip it.
    if (action === 'check-in' && !opts?.arrivalVerified && !opts?.interrupted) {
      const plan = planCheckInInterrupt([interruptRowFromAdminBooking(booking)]);
      if (plan.needed) {
        askInterrupt({
          key: booking.id,
          plan,
          onProceed: (result) => {
            const stopped = describeStopped(result.stopped);
            if (stopped) setActionError(stopped);
            const d = result.proceed[0];
            if (d) {
              void handleSimpleAction(booking, 'check-in', undefined, {
                arrivalVerified: d.arrivalVerified,
                heatConfirmed: d.heatConfirmed,
                interrupted: true,
              });
            }
          },
        });
        return;
      }
    }
    setActionError(null);
    setPending((prev) => new Set(prev).add(booking.id));

    const previous = state.bookings;
    const nextStatus =
      action === 'check-in'
        ? 'checked_in'
        : action === 'undo-check-in'
          ? 'confirmed'
          : // Ted #22 — reopening a check-out puts the dog back on the
            // floor; the check-IN still stands.
            action === 'undo-check-out'
            ? 'checked_in'
            : 'cancelled';
    setState({
      kind: 'ready',
      bookings: previous.map((b) =>
        b.id === booking.id ? { ...b, status: nextStatus } : b,
      ),
    });
    // Ted #27 part 2 (#988) — a forgotten visit is checked in on the day it
    // HAPPENED. Blank (every row on the day list) ⇒ the server stamps now,
    // exactly as before. The value is whatever is in the row's own arrival
    // field, so what the desk sees on screen is what is sent — and the
    // visibility question is asked through the SAME helper the render uses
    // (09-16 re-review, the major: a seeded value used to survive a tab
    // switch and be posted with no field on screen).
    const correctedArrival = correctedInstantToSend(
      correctedArrivalVisible(view),
      checkinTimeOverride[booking.id],
    );
    try {
      const updated =
        action === 'check-in'
          ? await checkInBooking(
              apiUrl,
              token,
              booking.id,
              correctedArrival,
              opts?.arrivalVerified,
              opts?.heatConfirmed,
            )
          : action === 'undo-check-in'
            ? await undoCheckInBooking(apiUrl, token, booking.id)
            : action === 'undo-check-out'
              ? await undoCheckOutBooking(apiUrl, token, booking.id, reason ?? '')
              : await cancelBooking(apiUrl, token, booking.id);
      applyBookingUpdate(updated);
      if (action === 'check-in' && correctedArrival === null) {
        runCardPrompt.offer([booking.id]);
      }
      // Sawyer #1296 — asked only after the check-in landed (the row as the
      // desk saw it carries the server's "already kept" flag).
      if (action === 'check-in') ownerNotesOfferRef.current([factsFromAdminBooking(booking)]);
    } catch (err) {
      rollbackTo(previous);
      // Ted #28 (#1050) — the dog arrives by transport and nobody has said
      // it is here yet. Not an error the desk did anything wrong about:
      // ask, then re-run with the answer.
      if (action === 'check-in' && isArrivalUnverified(err)) {
        askArrival({
          bookingId: booking.id,
          dogNames: booking.dog_names ?? [],
          onConfirm: () =>
            handleSimpleAction(booking, 'check-in', undefined, { arrivalVerified: true }),
        });
      } else {
        const message = err instanceof Error ? err.message : `Failed to ${action} booking`;
        setActionError(message);
      }
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(booking.id);
        return next;
      });
    }
  };

  // Lane LC — the QUIET checkout step: pops ONLY for credit / this visit
  // unpaid / other invoices (Ted 09-29), then continues into the existing
  // checkout untouched. Nothing else at checkout interrupts.
  const requestCheckout = (booking: AdminBooking, checkoutAction: CheckoutAction): void => {
    const plan = planCheckoutInterrupt([interruptRowFromAdminBooking(booking)]);
    if (!plan.needed) {
      void handleCheckoutAction(booking, checkoutAction);
      return;
    }
    askCheckout({
      key: `checkout:${booking.id}`,
      plan,
      onProceed: () => handleCheckoutAction(booking, checkoutAction),
    });
  };

  const requestHouseholdCheckout = (household: HouseholdGroup): void => {
    const plan = planCheckoutInterrupt(household.rows.map(interruptRowFromAdminBooking));
    if (!plan.needed) {
      startHouseholdCheckout(household);
      return;
    }
    askCheckout({
      key: `checkout:household:${household.customerId}`,
      plan,
      onProceed: () => startHouseholdCheckout(household),
    });
  };

  // Sawyer 13 household-checkout-grooming (C3) — after the interrupt step,
  // each groom's tier is confirmed inline, one step per grooming visit in
  // list order, and NOTHING is posted until the last step is confirmed
  // (cancel on any step posts nothing). A family with no groom goes straight
  // to the batch, exactly as before.
  const startHouseholdCheckout = (household: HouseholdGroup): void => {
    const queue = household.rows.filter((r) => household.groomingRowIds.includes(r.id));
    if (queue.length === 0) {
      void handleHouseholdCheckout(household, new Map());
      return;
    }
    setHouseholdTierSteps({ household, queue, index: 0, confirmed: new Map() });
  };

  /** Focus goes back to the card's button after the last step or a cancel (AC22). */
  const focusHouseholdButton = (customerId: string): void => {
    window.setTimeout(() => {
      document.querySelector<HTMLElement>(`[data-testid="household-check-out-all-${customerId}"]`)?.focus();
    }, 0);
  };

  const cancelHouseholdTierSteps = (): void => {
    const steps = householdTierSteps;
    setHouseholdTierSteps(null);
    if (steps) focusHouseholdButton(steps.household.customerId);
  };

  const confirmHouseholdTierStep = (args: {
    selectedTier: import('../lib/groomingTiers.js').GroomingTierDefinition;
    originalTier: import('../lib/groomingTiers.js').GroomingTierDefinition;
    finalTotalCents: number;
    operatorNotes: string | null;
  }): void => {
    const steps = householdTierSteps;
    const booking = steps?.queue[steps.index];
    if (!steps || !booking) return;
    const confirmed = new Map(steps.confirmed);
    confirmed.set(booking.id, {
      selectedTier: args.selectedTier,
      originalTier: args.originalTier,
      finalTotalCents: args.finalTotalCents,
      operatorNotes: args.operatorNotes,
      addOnIds: selectedRequestedAddOnIds(requestedAddOns),
      addOnLabels: requestedAddOnLabels(selectedRequestedAddOnIds(requestedAddOns)),
    });
    if (steps.index + 1 < steps.queue.length) {
      setHouseholdTierSteps({ ...steps, index: steps.index + 1, confirmed });
      return;
    }
    setHouseholdTierSteps(null);
    focusHouseholdButton(steps.household.customerId);
    void handleHouseholdCheckout(steps.household, confirmed);
  };

  /**
   * Sawyer C3 — turn the requested add-ons the desk left ticked into legs on
   * the groom BEFORE its check-out, so the check-out cascades them and the
   * money path invoices each once as its own line. Single-flight per booking.
   * Throws the server's message; the caller words it for its surface.
   */
  const applyConfirmedAddOns = async (bookingId: string, addOnIds: readonly string[]): Promise<void> => {
    if (addOnIds.length === 0) return;
    const { token } = readSession();
    if (!token) throw new Error('Your session has ended — sign in again.');
    const inFlight = addOnApplyInFlight.current.get(bookingId);
    if (inFlight) return inFlight;
    const run = applyRequestedAddOns(apiUrl, token, bookingId, addOnIds)
      .then(() => undefined)
      .finally(() => addOnApplyInFlight.current.delete(bookingId));
    addOnApplyInFlight.current.set(bookingId, run);
    return run;
  };

  /** The add-on labels for an apply failure sentence ("Nail Trim & Bath"). */
  const requestedAddOnLabels = (addOnIds: readonly string[]): string => {
    const state = requestedAddOns?.state;
    const labels =
      state?.kind === 'ok'
        ? state.requested.filter((r) => addOnIds.includes(r.addOnId)).map((r) => r.label)
        : [];
    return joinNames(labels) || 'the requested add-ons';
  };

  // Two-track checkout for non-grooming services (issue #25). Grooming
  // routes through the TierConfirmation modal which surfaces the same
  // settle-now / settle-later choice in its footer.
  const handleCheckoutAction = async (
    booking: AdminBooking,
    checkoutAction: CheckoutAction,
  ): Promise<void> => {
    const { token, staff } = readSession();
    if (!token || state.kind !== 'ready') return;
    const service = serviceTypeById.get(booking.service_type_id) ?? null;

    // Grooming bookings always go through the tier-confirmation modal.
    if (isGroomingServiceType(service?.name)) {
      setTierConfirm({
        booking,
        serviceType: service,
        bookingLabel: buildBookingLabel(booking),
        originalTierKey: extractBookedTierKey(booking),
        submitting: false,
      });
      return;
    }

    // Non-grooming two-track flow.
    setActionError(null);
    setPending((prev) => new Set(prev).add(booking.id));
    const previous = state.bookings;
    setState({
      kind: 'ready',
      bookings: previous.map((b) =>
        b.id === booking.id ? { ...b, status: 'checked_out' } : b,
      ),
    });

    // Check-out leg (operational — the dog leaves). A failure here rolls
    // the optimistic update back and stops before any money-path step.
    // Ted #6 — if the desk entered an actual check-out time (the dog left
    // earlier than it is being recorded), send it; the server bounds it.
    // Grooming has already routed to the modal above, so this row's field is
    // the one the day list has always drawn — asked through the shared
    // helper so the render and the send can never disagree.
    const correctedCheckedOutAt =
      correctedInstantToSend(
        correctedCheckoutVisible(view, false),
        checkoutTimeOverride[booking.id],
      ) ?? undefined;
    let updated: AdminBooking;
    try {
      updated =
        checkoutAction === 'settle-later'
          ? await checkOutBookingSettleLater({
              apiUrl,
              token,
              booking,
              staffId: staff?.id ?? 'unknown',
              staffUsername: staff?.username ?? null,
              checkedOutAt: correctedCheckedOutAt ?? null,
            })
          : await checkOutBooking(
              apiUrl,
              token,
              booking.id,
              undefined,
              correctedCheckedOutAt,
            );
      applyBookingUpdate(updated);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to check out booking';
      setActionError(message);
      rollbackTo(previous);
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(booking.id);
        return next;
      });
      return;
    }

    // Money path (issue #251/#252) — package redemption (exactly once,
    // both tracks) + open-invoice record + resolution. Never throws;
    // failures surface as a blocking dialog or notice, never silently.
    try {
      await completeMoneyPath({
        booking: updated,
        track: checkoutAction,
        service,
        totalCents: booking.total_cents,
        dogCount: dogCountOf(booking),
        correctedCheckedOutAt: correctedCheckedOutAt ?? null,
      });
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(booking.id);
        return next;
      });
    }
  };

  // Ted #2 follow-up (#905) + Ted #26 (#987) — check in one family's dogs
  // together from the Bookings list. One POST per booking on the existing
  // route (`POST /api/bookings/admin/:id/check-in`, unchanged), and ALL of
  // them settle before anything else happens, so a sibling the server
  // refuses — already checked in, cancelled, a vax gate — never stops the
  // rest. The grouping, the batching and the wording are the shared helpers
  // the dashboard and Front Desk use.
  //
  // Review fixes (#905/#987):
  //   - accepted rows fold in the server's record through
  //     `mergeActionResponse`, because the check-in payload emits the
  //     dog-first block at its EMPTY sentinels and a plain spread would blank
  //     the dog photos, the "Dog — Owner" line and the balance badge;
  //   - the button reads as busy for the whole batch. Deliberately NOT the
  //     optimistic flip a single Check In does: flipping the rows here would
  //     drop the family below two arrivals and REMOVE THE CARD — including
  //     the control the desk just pressed — before the server had agreed, and
  //     a refusal would then have to re-materialise it. The card staying put
  //     with a busy label is the honest feedback for a batch;
  //   - the successes are told in status tone; only refusals reach the error
  //     banner. A full success is confirmed instead of being silent.
  const handleHouseholdCheckIn = async (
    rows: readonly ArrivalRow[],
    /** Lane LC — the "Before you check in" step's per-booking decisions, once it has run. */
    decisions?: ReadonlyMap<string, InterruptDecision>,
    /** Lane LC — the dogs the step stopped, named on the same banner as the batch's refusals. */
    stoppedNote: string | null = null,
  ): Promise<void> => {
    const { token } = readSession();
    if (!token || rows.length === 0 || state.kind !== 'ready') return;
    // Lane LC — the household's UNION of check-in alerts, once, before any
    // POST. A Do Not Admit dog is named and left out; the siblings go in.
    if (!decisions) {
      const plan = planCheckInInterrupt(rows.map((r) => interruptRowFromAdminBooking(r.booking)));
      if (plan.needed) {
        askInterrupt({
          key: `household:${rows[0]!.customerId}`,
          plan,
          onProceed: (result) => {
            const stopped = describeStopped(result.stopped);
            const map = new Map(result.proceed.map((d) => [d.bookingId, d] as const));
            const go = rows.filter((r) => map.has(r.bookingId));
            if (go.length > 0) void handleHouseholdCheckIn(go, map, stopped);
            else if (stopped) setActionError(stopped);
          },
        });
        return;
      }
    }
    setActionError(null);
    setCheckInNotice(null);
    const ids = rows.map((r) => r.bookingId);
    const customerIds = [...new Set(rows.map((r) => r.customerId))];
    setPending((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
    setHouseholdCheckInBusy((prev) => {
      const next = new Set(prev);
      for (const cid of customerIds) next.add(cid);
      return next;
    });
    const updates = new Map<string, AdminBooking>();
    try {
      const outcome = await checkInHousehold(rows, async (row) => {
        // Ted #27 part 2 (#988), 09-16 re-review — the batch sends each
        // dog's OWN corrected arrival, exactly as its single-row Check In
        // does. This is the button the desk actually presses on a list of
        // forgotten siblings (the card's "Check in all", and the #987
        // sibling prompt, which routes here too). Sending nothing here let
        // the server stamp NOW for a visit whose visible, pre-filled
        // arrival said 2026-08-29 — and the pre-filled original-date
        // check-out was then refused 422 `before_check_in`, so the only way
        // through put the charge on today. That is the bug this PR exists
        // to remove, reached through its most-pressed control.
        //
        // Per row, never one instant for the family: two forgotten visits
        // of the same owner can be weeks apart, so each lands on its own
        // day. Blank / not on screen ⇒ null ⇒ the server stamps now, which
        // is every row on the day list (unchanged).
        const decision = decisions?.get(row.bookingId);
        const updated = await checkInBooking(
          apiUrl,
          token,
          row.bookingId,
          correctedInstantToSend(
            correctedArrivalVisible(view),
            checkinTimeOverride[row.bookingId],
          ),
          decision?.arrivalVerified,
          decision?.heatConfirmed,
        );
        updates.set(row.bookingId, updated);
        return updated;
      });
      if (updates.size > 0) {
        setState((curr) =>
          curr.kind === 'ready'
            ? {
                kind: 'ready',
                bookings: curr.bookings.map((b) => {
                  const updated = updates.get(b.id);
                  return updated ? mergeActionResponse(b, updated) : b;
                }),
              }
            : curr,
        );
      }
      const { ok, problem } = describeHouseholdCheckIn(outcome);
      const problems = [stoppedNote, problem].filter(Boolean).join(' ');
      if (problems) setActionError(problems);
      if (ok) setCheckInNotice(ok);
      if (outcome.succeeded.length > 0) {
        // Ted Item 34 — one prompt for the whole family's cards.
        runCardPrompt.offer(outcome.succeeded.map((r) => r.bookingId));
        // Sawyer #1296 — ONE owner's-notes question for the family's notes.
        ownerNotesOfferRef.current(
          outcome.succeeded
            .map((r) => state.bookings.find((b) => b.id === r.bookingId))
            .filter((b): b is AdminBooking => b !== undefined)
            .map(factsFromAdminBooking),
        );
      }
      // Ted #28 (#1050) — THE BATCH RULE. "Check in all" never sweeps a
      // transported dog in; each one gets its own question, in turn, while
      // its walk-in siblings are already checked in above.
      for (const row of outcome.needsArrivalCheck) {
        const target = state.bookings.find((b) => b.id === row.bookingId);
        if (!target) continue;
        askArrival({
          bookingId: row.bookingId,
          dogNames: row.dogNames,
          onConfirm: () =>
            handleSimpleAction(target, 'check-in', undefined, { arrivalVerified: true }),
        });
      }
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
      setHouseholdCheckInBusy((prev) => {
        const next = new Set(prev);
        for (const cid of customerIds) next.delete(cid);
        return next;
      });
    }
  };

  // Ted #10 (#913) — check out one household's dogs together. Per visit the
  // SAME legs as the single row (check-out → the row's package choice →
  // invoice), one after another so a failure on one dog never stops the
  // others; then ONE settle dialog for the family. The dialog's cart read
  // returns every invoice just queued and "Collect the whole visit" is on
  // by default, so the desk records one payment and the customer gets one
  // receipt. Rows stay individually actionable; this is never a
  // cross-customer select (the server refuses a mixed cart).
  const handleHouseholdCheckout = async (
    household: HouseholdGroup,
    /** Sawyer C3 — each groom's confirmed tier step, by booking id. */
    tierSteps: ReadonlyMap<string, HouseholdTierStepResult>,
  ): Promise<void> => {
    const { token } = readSession();
    if (!token || state.kind !== 'ready') return;
    setActionError(null);
    const ids = household.rows.map((b) => b.id);
    setPending((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
    const collected: Array<{ booking: AdminBooking; invoice: AdminOpenInvoice }> = [];
    const checkoutFailures: Array<{ dogs: string; error: string }> = [];
    // Sawyer C3 — a groom whose requested add-ons could not be added is NOT
    // checked out (nothing billed that the desk could not record); its own
    // sentence, since the fix is different from a refused check-out.
    const addOnFailures: string[] = [];
    const moneyFailures: MoneyRecordFailureState[] = [];
    let covered = 0;
    try {
      for (const booking of household.rows) {
        const service = serviceTypeById.get(booking.service_type_id) ?? null;
        const dogs = joinNames(booking.dog_names ?? []) || `#${shortId(booking.id)}`;
        // Sawyer 13 household-checkout-grooming (C3) — a groom's tier step.
        const isGroom = household.groomingRowIds.includes(booking.id);
        const step = isGroom ? tierSteps.get(booking.id) : undefined;
        if (isGroom && !step) {
          checkoutFailures.push({ dogs, error: 'its grooming tier was not confirmed' });
          continue;
        }
        if (step && step.addOnIds.length > 0) {
          try {
            await applyConfirmedAddOns(booking.id, step.addOnIds);
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Could not add the requested add-ons';
            addOnFailures.push(
              `Could not add ${step.addOnLabels} for ${dogs} (${message}). ${dogs} was not checked out — check that visit out from its own row.`,
            );
            continue;
          }
        }
        // Optimistic flip for THIS row only. A failure reverts THIS row only:
        // a sibling the server already checked out must stay checked out.
        setState((curr) =>
          curr.kind === 'ready'
            ? {
                kind: 'ready',
                bookings: curr.bookings.map((b) =>
                  b.id === booking.id
                    ? { ...b, status: 'checked_out', ...(step ? { total_cents: step.finalTotalCents } : {}) }
                    : b,
                ),
              }
            : curr,
        );
        // Per ROW, not per batch: a household can mix a grooming visit with
        // a daycare one, and the grooming field is drawn only on the
        // Outstanding list. Same helper as the render.
        const correctedCheckedOutAt =
          correctedInstantToSend(
            correctedCheckoutVisible(view, isGroomingServiceType(service?.name)),
            checkoutTimeOverride[booking.id],
          ) ?? undefined;
        let updated: AdminBooking;
        try {
          if (step) {
            // Sawyer C3 — the groom checks out with its confirmed tier: the
            // same payload + `final_total_cents` as the single row, then the
            // same best-effort tier audit (a failure there never blocks).
            const { staff } = readSession();
            const payload = buildTierConfirmationPayload({
              bookingTotalCents: booking.total_cents,
              originalTier: step.originalTier,
              selectedTier: step.selectedTier,
              finalTotalCents: step.finalTotalCents,
              staffId: staff?.id ?? 'unknown',
              staffUsername: staff?.username ?? null,
              operatorNotes: step.operatorNotes,
            });
            updated = (
              await checkOutGroomingBooking(
                apiUrl,
                token,
                booking.id,
                payload,
                step.operatorNotes,
                correctedCheckedOutAt ?? null,
              )
            ).booking;
            const audit = await adminPaymentsApi.recordTierAudit(apiUrl, {
              bookingId: booking.id,
              originalTier: payload.original_tier,
              finalTier: payload.final_tier,
              originalTierLabel: payload.original_tier_label,
              finalTierLabel: payload.final_tier_label,
              originalTotalCents: payload.original_total_cents,
              finalTotalCents: payload.final_total_cents,
              operatorNotes: payload.operator_notes,
              confirmedAt: payload.confirmed_at,
            });
            if (audit.error) {
              console.warn(
                `handleHouseholdCheckout: tier-audit POST failed for booking ${booking.id}: ${audit.error}`,
              );
            }
          } else {
            updated = await checkOutBooking(
              apiUrl,
              token,
              booking.id,
              undefined,
              correctedCheckedOutAt,
            );
          }
          applyBookingUpdate(updated);
        } catch (err) {
          checkoutFailures.push({
            dogs,
            error: err instanceof Error ? err.message : 'Failed to check out booking',
          });
          setState((curr) =>
            curr.kind === 'ready'
              ? {
                  kind: 'ready',
                  bookings: curr.bookings.map((b) =>
                    b.id === booking.id
                      ? { ...b, status: booking.status, total_cents: booking.total_cents }
                      : b,
                  ),
                }
              : curr,
          );
          continue;
        }
        const result = await completeMoneyPath(
          {
            booking: updated,
            track: 'settle-now',
            service,
            // Sawyer C3 — a groom's invoice is its confirmed TIER total.
            totalCents: step ? step.finalTotalCents : booking.total_cents,
            ...(step ? { operatorNotes: step.operatorNotes } : {}),
            dogCount: dogCountOf(booking),
            correctedCheckedOutAt: correctedCheckedOutAt ?? null,
          },
          { deferDialog: true },
        );
        switch (result.kind) {
          case 'invoice':
            collected.push({ booking: updated, invoice: result.invoice });
            break;
          case 'covered':
            covered += 1;
            break;
          case 'failed':
            moneyFailures.push({ ...result.args, error: result.error });
            break;
          default:
            // 'halted' raised its own alert; the rest cannot occur deferred.
            break;
        }
      }
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
    }
    if (checkoutFailures.length > 0) {
      const done = collected.length + covered + moneyFailures.length;
      setActionError(
        [
          `Could not check out ${joinNames(checkoutFailures.map((f) => f.dogs))} (${checkoutFailures
            .map((f) => f.error)
            .join('; ')}).${done > 0 ? ' The other visits were checked out.' : ''} Check ${
            checkoutFailures.length === 1 ? 'that visit' : 'those visits'
          } out from ${checkoutFailures.length === 1 ? 'its' : 'their'} own row.`,
          ...addOnFailures,
        ].join(' '),
      );
    } else if (addOnFailures.length > 0) {
      setActionError(addOnFailures.join(' '));
    }
    if (moneyFailures.length > 0) {
      setQueuedMoneyFailures((prev) => [...prev, ...moneyFailures]);
    }
    const first = collected[0];
    if (first) {
      setSettleDialog({
        booking: first.booking,
        bookingLabel: householdLabel(household),
        invoice: first.invoice,
        idempotencyKey: makeIdempotencyKey('settle-now', first.booking.id),
        household: {
          dogNames: uniqueDogNames(collected.map((c) => c.booking)),
          visits: collected.length,
        },
      });
    } else if (covered > 0 && moneyFailures.length === 0 && checkoutFailures.length === 0) {
      setSettleNotice(
        `${householdLabel(household)} checked out — ${
          covered === 1 ? 'the visit is' : `all ${covered} visits are`
        } covered by package days; nothing to collect.`,
      );
    }
  };

  const handleTierConfirm = useCallback(
    async (args: {
      selectedTier: import('../lib/groomingTiers.js').GroomingTierDefinition;
      originalTier: import('../lib/groomingTiers.js').GroomingTierDefinition;
      finalTotalCents: number;
      operatorNotes: string | null;
      checkoutAction: CheckoutAction;
    }): Promise<void> => {
      const { token, staff } = readSession();
      if (!token || !tierConfirm || state.kind !== 'ready') return;
      const previous = state.bookings;
      const booking = tierConfirm.booking;

      setTierConfirm({ ...tierConfirm, submitting: true });
      setPending((prev) => new Set(prev).add(booking.id));

      // Sawyer 13 household-checkout-grooming (C3) — the requested add-ons
      // the desk left ticked become legs BEFORE the check-out, so it cascades
      // them and each is invoiced once as its own line. A failure keeps the
      // dialog open with the reason and checks nothing out.
      const addOnIds = selectedRequestedAddOnIds(requestedAddOns);
      if (addOnIds.length > 0) {
        try {
          await applyConfirmedAddOns(booking.id, addOnIds);
        } catch (err) {
          const dogs = joinNames(booking.dog_names ?? []) || `#${shortId(booking.id)}`;
          const message = err instanceof Error ? err.message : 'Could not add the requested add-ons';
          setTierConfirm({ ...tierConfirm, submitting: false });
          setPending((prev) => {
            const next = new Set(prev);
            next.delete(booking.id);
            return next;
          });
          throw new Error(
            `Could not add ${requestedAddOnLabels(addOnIds)} for ${dogs} (${message}). ${dogs} was not checked out — check that visit out from its own row.`,
          );
        }
      }

      // Optimistic update — mark checked_out + new total.
      setState({
        kind: 'ready',
        bookings: previous.map((b) =>
          b.id === booking.id
            ? { ...b, status: 'checked_out', total_cents: args.finalTotalCents }
            : b,
        ),
      });

      const payload = buildTierConfirmationPayload({
        bookingTotalCents: booking.total_cents,
        originalTier: args.originalTier,
        selectedTier: args.selectedTier,
        finalTotalCents: args.finalTotalCents,
        staffId: staff?.id ?? 'unknown',
        staffUsername: staff?.username ?? null,
        operatorNotes: args.operatorNotes,
      });

      // Ted #6 / #27 (#909 · #988) — a grooming visit completed from the
      // Outstanding list belongs to the day it happened. The row's
      // corrected-check-out value (pre-filled with the service day on that
      // list, blank on the day list) rides BOTH grooming tracks; blank ⇒
      // the server records now, exactly as before.
      // 09-16 re-review, the major: `view` is part of the question. On the
      // DAY list this field is not drawn for a grooming row, so a value left
      // over from the Outstanding list must NOT ride along — the desk would
      // have no way to see or correct the day the charge landed on.
      const groomingCheckedOutAt =
        correctedInstantToSend(
          correctedCheckoutVisible(view, true),
          checkoutTimeOverride[booking.id],
        ) ?? undefined;
      let updated: AdminBooking;
      try {
        if (args.checkoutAction === 'settle-later') {
          updated = await checkOutBookingSettleLater({
            apiUrl,
            token,
            booking,
            staffId: staff?.id ?? 'unknown',
            staffUsername: staff?.username ?? null,
            operatorNotes: args.operatorNotes,
            tierConfirmation: payload,
            checkedOutAt: groomingCheckedOutAt ?? null,
          });
        } else {
          const result = await checkOutGroomingBooking(
            apiUrl,
            token,
            booking.id,
            payload,
            args.operatorNotes,
            groomingCheckedOutAt ?? null,
          );
          updated = result.booking;
        }
        // Record the tier-change to the server-of-record (issue #140, AC6).
        // BEST-EFFORT: the check-out already succeeded, and a desk role
        // lacking payments-ops:write would 403 here; either way we must not
        // fail the checkout. Mirrors the old non-blocking localStorage append.
        const audit = await adminPaymentsApi.recordTierAudit(apiUrl, {
          bookingId: booking.id,
          originalTier: payload.original_tier,
          finalTier: payload.final_tier,
          originalTierLabel: payload.original_tier_label,
          finalTierLabel: payload.final_tier_label,
          originalTotalCents: payload.original_total_cents,
          finalTotalCents: payload.final_total_cents,
          operatorNotes: payload.operator_notes,
          confirmedAt: payload.confirmed_at,
        });
        if (audit.error) {
          console.warn(
            `handleTierConfirm: tier-audit POST failed for booking ${booking.id}: ${audit.error}`,
          );
        }
        applyBookingUpdate(updated);
        setTierConfirm(null);
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to confirm grooming check-out';
        setActionError(message);
        rollbackTo(previous);
        if (tierConfirm) {
          setTierConfirm({ ...tierConfirm, submitting: false });
        }
        setPending((prev) => {
          const next = new Set(prev);
          next.delete(booking.id);
          return next;
        });
        throw err;
      }

      // Money path (issue #251/#252) — same shared pipeline as the
      // non-grooming flow, with the FINAL (post-tier) total. Never throws.
      try {
        await completeMoneyPath({
          booking: updated,
          track: args.checkoutAction,
          service: tierConfirm.serviceType,
          totalCents: args.finalTotalCents,
          operatorNotes: args.operatorNotes,
          dogCount: dogCountOf(booking),
        });
      } finally {
        setPending((prev) => {
          const next = new Set(prev);
          next.delete(booking.id);
          return next;
        });
      }
    },
    [
      apiUrl,
      tierConfirm,
      state,
      checkoutTimeOverride,
      // 09-16 re-review, the major: the corrected check-out this callback
      // forwards is only on screen for a grooming row on the Outstanding
      // list, so `view` is part of what it computes — a stale closure here
      // would re-open the invisible backdated write.
      view,
      applyBookingUpdate,
      rollbackTo,
      completeMoneyPath,
      // Sawyer C3 — the ticked add-ons this confirm applies.
      requestedAddOns,
    ],
  );

  const sorted = useMemo(() => {
    if (state.kind !== 'ready') return [];
    // Ted #27 (#988) — the outstanding feed is already ordered oldest-first
    // by the server (last service day, then created_at). Re-sorting it by
    // start_time here would scramble weeks of dates into a meaningless
    // clock order, so the client leaves it alone.
    if (view === 'outstanding') return state.bookings;
    // The day view sorts by start time — every row is the same day, so the
    // time IS the order. The approval queue (Ted #26 / #923) spans dates, and
    // a start-time sort there interleaves next month with this morning; it
    // orders by date first, oldest waiting reservation at the top.
    if (pendingQueue) return sortOldestFirst(state.bookings);
    // Ted #32c / E3.3 — a find spans dates: only the rows the desk typed, newest first.
    if (finding) {
      return sortNewestFirst(
        searchActive
          ? state.bookings.filter((b) => bookingMatchesSearch(b, searchQuery))
          : state.bookings,
      );
    }
    return [...state.bookings].sort((a, b) =>
      (a.start_time ?? '').localeCompare(b.start_time ?? ''),
    );
  }, [state, pendingQueue, view, finding, searchActive, searchQuery]);

  // Ted #10 (#913) — the household: one owner's checked-in visits, grouped
  // so the desk checks them out together (mirrors the open-invoices queue's
  // per-owner cart). Sawyer C3 — the groom joins the card; its tier is
  // confirmed inline when "Check out all" is pressed. Legs never group, the
  // Outstanding list never groups, one visit alone is not a household
  // (lib/household-checkout.ts).
  const households = useMemo(
    () =>
      groupCheckoutHouseholds(
        sorted,
        (b) => isGroomingServiceType(serviceTypeById.get(b.service_type_id)?.name),
        view,
      ),
    [sorted, serviceTypeById, view],
  );

  // Ted #2 follow-up (#905) — the ARRIVALS households: the same grouping on
  // the other side of the day. On 09-14 Ted checked one family's two dogs in
  // one at a time because this card existed only on Dashboard → Arrivals and
  // Front Desk; the Bookings list is where he was.
  //
  // KNOWN LIMIT (#905/#987 review): `sorted` comes from `load()`, which
  // passes `serviceTypeId` to the server. With a service filter on, a
  // family's other-service rows are not in this list, so the card does not
  // appear and the #987 prompt does not fire. The copy on both surfaces
  // names the filter rather than claiming a fact about the whole day.
  const arrivalHouseholdsByCustomer = useMemo(() => arrivalHouseholds(sorted), [sorted]);

  /** Ted #26 (#987) — this dog's family, minus this dog. Empty = no prompt. */
  const arrivalSiblingsOf = (b: AdminBooking): AdminBooking[] => {
    const household = arrivalHouseholdsByCustomer.get(b.customer_id);
    if (!household) return [];
    const rows = household.rows.filter((r) => r.bookingId !== b.id).map((r) => r.booking);
    // Ted #32c / E3.3 — a FIND spans months, so "the same owner's other
    // pending visits" is no longer "the other dogs arriving today". Only the
    // dogs booked for THIS visit's day are family for the picker; a visit on
    // another day is never offered a tick it could be checked in by.
    if (!finding) return rows;
    const day = (x: AdminBooking): string => (x.start_date ?? x.date ?? '').slice(0, 10);
    return rows.filter((r) => day(r) === day(b));
  };

  // The list in display order: a household's rows travel together at the
  // slot of its first row; every other row keeps its position. A booking has
  // exactly one status, so it can belong to the check-OUT household (checked
  // in) or the check-IN one (pending/confirmed), never both — an owner with
  // dogs on both sides gets both cards, each at its own slot.
  //
  // This DOES move rows on the Outstanding list (09-16 re-review): an
  // owner's two forgotten arrivals gather at the OLDEST of the two, so the
  // list is oldest-first per household rather than strictly per row. The
  // trade is deliberate — every row still shows its own original date and
  // its own overdue count, and the family is checked in together, which is
  // what Ted asked for.
  const displayEntries = useMemo<DisplayEntry[]>(() => {
    // THE APPROVAL QUEUE IS NOT A DAY (Ted #26 / #923). The queue's rows are
    // DECISIONS, not arrivals — five of Kate's reservations on five future
    // dates, each confirmed or not on its own merits. Grouping there would
    // put them behind one "Check in all 5 family dogs" card for dogs that
    // are not coming today, and would drag the other
    // reservations — five different dates — behind one "Check in all 5 family
    // dogs" card that is false about the day, and would drag the other
    // households' rows out of the oldest-first order the queue exists to
    // provide. In the queue every reservation is its own row and its own
    // decision.
    if (pendingQueue || finding) return sorted.map((b) => ({ kind: 'row', booking: b }) as const);
    const placed = new Set<string>();
    const out: DisplayEntry[] = [];
    for (const b of sorted) {
      if (placed.has(b.id)) continue;
      const household = households.get(b.customer_id);
      if (household && household.rows.some((r) => r.id === b.id)) {
        for (const r of household.rows) placed.add(r.id);
        out.push({ kind: 'household', household });
        continue;
      }
      const arrivals = arrivalHouseholdsByCustomer.get(b.customer_id);
      if (arrivals && arrivals.rows.some((r) => r.bookingId === b.id)) {
        for (const r of arrivals.rows) placed.add(r.bookingId);
        out.push({ kind: 'arrival-household', household: arrivals });
        continue;
      }
      placed.add(b.id);
      out.push({ kind: 'row', booking: b });
    }
    return out;
  }, [pendingQueue, finding, sorted, households, arrivalHouseholdsByCustomer]);

  // Ted #10 (#913) — the queued household failures block one at a time,
  // only while no other dialog is up.
  useEffect(() => {
    if (settleDialog || moneyRecordFailure || queuedMoneyFailures.length === 0) return;
    const [next, ...rest] = queuedMoneyFailures;
    if (!next) return;
    setQueuedMoneyFailures(rest);
    setMoneyRecordFailure(next);
  }, [settleDialog, moneyRecordFailure, queuedMoneyFailures]);

  // Bookings that count as "boarders today" for the print-run-cards
  // feature. Filters by the service-type name (case-insensitive contains
  // 'board' / 'overnight' / 'lodging') and excludes cancelled bookings.
  const boardingBookings = useMemo<AdminBooking[]>(() => {
    if (state.kind !== 'ready') return [];
    return state.bookings.filter((b) => {
      // Ted #32c — a no-show boarder never arrived; no run card for it either.
      if (b.status === 'cancelled' || b.status === 'no_show') return false;
      const svc = serviceTypeById.get(b.service_type_id);
      return isBoardingServiceType(svc?.name);
    });
  }, [state, serviceTypeById]);

  const handlePrintRunCards = useCallback(async (): Promise<void> => {
    const { token } = readSession();
    const dateLabel = new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    if (!token || boardingBookings.length === 0) {
      setRunCardsView({ cards: [], dateLabel });
      return;
    }
    setRunCardsLoading(true);
    setRunCardsError(null);
    try {
      // #549 — one tenant-scoped server call assembles every card (a card
      // per boarding booking present on `date`, per dog, with feeding +
      // structured medications for the day-grid). The server owns booking→
      // dog association and mid-stay-boarder selection, so this replaces the
      // old per-customer N+1 fetch.
      const { cards: apiCards } = await fetchRunCards(apiUrl, token, date);
      setRunCardsView({
        cards: apiCards.map(apiCardToRunCard),
        dateLabel,
      });
    } catch (err) {
      const message =
        err instanceof Error ? humanizeApiError(err.message) : 'Failed to build run cards';
      setRunCardsError(message);
    } finally {
      setRunCardsLoading(false);
    }
  }, [apiUrl, boardingBookings.length, date]);

  /**
   * Ted #46 (#1218) — the deck went to the printer: record each visit's card
   * as printed. Best-effort (the paper is already printing; a failed record
   * only means the next reprint shows today's card).
   */
  const recordDeckPrinted = useCallback(() => {
    const { token } = readSession();
    if (!token || !runCardsView || runCardsView.cards.length === 0) return;
    void recordRunCardsPrinted(apiUrl, token, runCardsView.cards, {
      refresh: runCardsView.live === true,
    }).catch(() => undefined);
  }, [apiUrl, runCardsView]);

  /** Ted #46 — "Print today's version": reload the deck as `live`. */
  const loadLiveDeck = useCallback(async (): Promise<void> => {
    const { token } = readSession();
    if (!token || !runCardsView) return;
    try {
      const { cards: apiCards } = await fetchRunCards(apiUrl, token, date, { live: true });
      setRunCardsView({ ...runCardsView, cards: apiCards.map(apiCardToRunCard), live: true });
    } catch (err) {
      setRunCardsError(
        err instanceof Error ? humanizeApiError(err.message) : 'Failed to build run cards',
      );
    }
  }, [apiUrl, date, runCardsView]);

  /**
   * "28 days ago" for an outstanding row, or '' when the facility day is
   * unknown (an older server that answers without `before`). Never invents
   * a number from the browser's clock — the server owns which day is
   * "today" for this facility.
   */
  const overdueLabel = (b: AdminBooking): string => {
    if (outstanding.kind !== 'ready' || !outstanding.before) return '';
    const days = daysOverdue(lastServiceDayIso(b), outstanding.before);
    if (days === null || days <= 0) return '';
    return days === 1 ? '1 day ago' : `${days} days ago`;
  };

  // Ted #32c (#1158) — how a row describes itself to the shared Cancel / Edit /
  // No-show dialogs: dogs and owner, never an id; the full row so Edit needs no
  // second read; the facility day it is listed under.
  const reservationTargetOf = (b: AdminBooking): ReservationTarget => ({
    bookingId: b.id,
    label: bookingDisplayLabel(b),
    dogLabel: joinNames(b.dog_names ?? []) || b.customer_name || 'This dog',
    customerId: b.customer_id,
    day: (b.start_date ?? b.date ?? '').slice(0, 10),
    hasComboPartner: Boolean(b.linked_booking_id),
    booking: b,
  });

  // Ted #10 (#913) — one booking row. Rendered at the top level AND inside
  // a household card, so it is a function rather than an inline map.
  const renderRow = (b: AdminBooking): JSX.Element => {
    const todayLocal = todayIsoDate();
    // Ted #32c (#1158) — No-show only once the reservation's first day has come.
    const actions = nextActions(b.status, b.isLeg === true).filter(
      (a) => a !== 'no-show' || firstDayReached(b, todayLocal),
    );
    const isPending = pending.has(b.id) || confirmFlow.busyIds.has(b.id);
    const service = serviceTypeById.get(b.service_type_id) ?? null;
    const isGrooming = isGroomingServiceType(service?.name);
    const supportsCheckout = actions.includes('check-out');
    const dropOff = b.drop_off_time;
    const pickUp = b.pick_up_time;
    const hasTimeWindow = dropOff || pickUp;
    // Issue #26/#140 — preview "3 days → 2 days" when the
    // customer has an active package matching this booking's
    // service. Package lookups are now async (server-of-record),
    // so this reads from `activePackageByKey`, which is
    // pre-loaded in an effect for all visible bookings — keeping
    // the render path synchronous (choice (a) from the brief).
    const packageKind = packageKindForService(service?.name);
    const activePackages = packageKind
      ? activePackageByKey.get(packageKey(b.customer_id, packageKind)) ?? []
      : [];
    // Post-void coherence (audit P1): the booking's only payment
    // was voided/refunded with nothing collected. Only meaningful
    // while the booking is still live — a cancelled booking is
    // already coherent with a reversed payment.
    const reversedPayment =
      b.status !== 'cancelled'
        ? reversedBookings.get(b.id) ?? null
        : null;
    const canCancel = actions.includes('cancel');
    // Ted #11 (#914) — absent for every row that is not a checkout-capable
    // boarder; the control renders nothing for those.
    const latePickupState = latePickupByBooking[b.id];
    // #1157 E2.5 — the proposal as of the corrected checkout time on this
    // row, when the desk has typed one: corrected to 10:50, an 11:20 "late"
    // is not late, and the row says so instead of offering the charge. The
    // field asked through the same helpers the submit path uses, so what is
    // drawn is what is sent.
    const correctedLate = applyCorrectedCheckout(
      latePickupState?.status === 'ready' ? latePickupState.proposal : null,
      correctedInstantToSend(
        correctedCheckoutVisible(view, isGrooming),
        checkoutTimeOverride[b.id],
      ),
    );
    // Ted #26 (#987) — the approval queue spans -180/+365 days and the day
    // picker is disabled while it is open, so a row that renders only a clock
    // time cannot be told apart from another day's identical slot. In the
    // queue the row leads with the DATE; in the day view (one date, already
    // on screen in the picker) it leads with the time, unchanged.
    const whenLabel = bookingWhenLabel(b, { withDate: pendingQueue || finding });

    return (
      <li
        key={b.id}
        id={`booking-row-${b.id}`}
        className={`ferro-table__row${
          b.id === focusBookingId ? ' ferro-table__row--focus' : ''
        }`}
      >
        <div className="ferro-table__body">
          {/* #545/#546 — dog-first: lead with the booked dog(s)
              (photo/name/breed + status strip), owner secondary.
              Falls back to the legacy owner-led text line when a
              stale server omits the `dogs` payload (deploy-order
              safety), then to the short id. */}
          <div className="ferro-table__primary">
            {b.dogs && b.dogs.length > 0 ? (
              <DogFirstCell
                dogs={b.dogs}
                ownerName={b.customer_name ?? `#${shortId(b.id)}`}
                customerId={b.customer_name ? b.customer_id : null}
                prefix={whenLabel}
                unpaid={b.unpaid}
                balanceDueCents={b.balance_due_cents}
                avatarSize="sm"
                apiUrl={apiUrl}
              />
            ) : (
              <>
                {whenLabel ? `${whenLabel} · ` : ''}
                {b.dog_names && b.dog_names.length > 0
                  ? `🐕 ${b.dog_names.join(', ')}${b.customer_name ? ` — ${b.customer_name}` : ''}`
                  : b.customer_name
                    ? b.customer_name
                    : `#${shortId(b.id)}`}
              </>
            )}
          </div>
          <div className="ferro-table__meta">
            {/* Ted #32c — a no-show was never billed; say so rather than let
                the booked total read as money owed. */}
            {statusLabel(b.status)} ·{' '}
            {b.status === 'no_show' ? 'no charge' : formatMoney(b.total_cents)}
            {/* Ted #29 — a leg reads as the add-on it IS ("Walk"), never as
                the service type it rides on ("Grooming"). */}
            {service ? ` · ${b.isLeg && b.add_on_name ? b.add_on_name : service.display_name}` : ''}
            {` · #${shortId(b.id)}`}
          </div>
          {/* Ted #29 (#1108) — the per-date line: date, performed by, price,
              each editable on this date alone.
              DEPLOY-WINDOW RENDER GUARD (L0 review): frontends auto-deploy on
              merge and the server waits for Johnny's "deploy", so for a while
              this app runs against a server without PATCH /:id/legs/:legId.
              `assigned_staff_id` is emitted by the NEW bookingView only (as a
              value or null); an older server omits the key, and
              fetchAdminSchedule passes rows through untouched, so `undefined`
              here means "old server" and the line — with its two controls
              that would 403 `route_not_mapped` — does not render. */}
          {b.isLeg && b.parent_booking_id && b.assigned_staff_id !== undefined ? (
            <ServiceLegLine apiUrl={apiUrl} token={sessionToken} leg={b} onChanged={() => void load()} />
          ) : null}
          {/* Ted 10-09 (#915) — a desk booking the client was never told
              about says so on the row (and on the deep-linked row a search
              opens). Nothing renders for "not applicable" or "unknown". */}
          <ClientNotNotifiedPill
            notified={b.customer_notified}
            status={b.status}
            testId={`booking-client-not-notified-${b.id}`}
          />
          {/* Sawyer 10 customer-booking-verify-policy (C5 C6) — owner care pills (lane 10) */}
          <OwnerCareCheckPill
            careAttestedAt={b.care_attested_at}
            medicationPolicyAckAt={b.medication_policy_ack_at}
            testId={`booking-owner-care-${b.id}`}
          />
          {/* LG (board Contracts §3) — a pending row HELD for staff review
              says so on the row, in the server's words; Confirm opens the
              review dialog, where a manager clears it with a reason. */}
          {b.review_held && b.review_hold_reason ? (
            <div
              className="ferro-table__meta ferro-table__hold"
              role="status"
              data-testid={`booking-review-hold-${b.id}`}
            >
              <strong>Needs staff review:</strong> {b.review_hold_reason}
            </div>
          ) : null}
          {/* Ted #30 (lane LR) — DEPLOY-WINDOW RENDER GUARD: `series_id` is a
              key only the new server emits; absent ⇒ nothing renders. */}
          {b.series_id ? (
            <button
              type="button"
              className="ferro-table__series"
              onClick={() => setSeriesView(b.series_id!)}
              data-testid={`booking-series-${b.id}`}
            >
              Part of a recurring series{b.series_detached_at ? ' (changed on its own)' : ''} · View series
            </button>
          ) : null}
          {/* Ted #27 part 2 (#988) — on the Outstanding list the row's own
              DATE is the thing the desk cannot see anywhere else, so it
              leads this line, with how overdue it is in plain language and
              where the reservation came from (the Gingr id is what the desk
              types into Gingr to check the visit out there first). */}
          {view === 'outstanding' ? (
            <div
              className="ferro-table__meta ferro-outstanding__origin"
              data-testid={`outstanding-origin-${b.id}`}
            >
              {formatServiceDay(lastServiceDayIso(b))}
              {overdueLabel(b) ? ` · ${overdueLabel(b)}` : ''}
              {` · ${sourceLabel(b)}`}
            </div>
          ) : null}
          {/* Ted #32c / E3.3 (#1158) — a checked-out visit has one action, and it
              is not obvious: the row cannot be edited (that would rewrite the
              money), it is REOPENED, changed, and checked out again. Said on
              the row, beside the one button that does it. */}
          {actions.includes('undo-check-out') ? (
            <div
              className="ferro-table__meta"
              data-testid={`booking-checked-out-hint-${b.id}`}
            >
              Checked out. To change anything — the time, the price, a package day —
              tap <strong>Reopen checkout</strong>, make the change, then check out again.
            </div>
          ) : null}
          {hasTimeWindow ? (
            <div
              className="ferro-table__meta"
              data-testid={`booking-time-window-${b.id}`}
            >
              {dropOff ? `Drop-off ${dropOff}` : null}
              {dropOff && pickUp ? ' · ' : null}
              {pickUp ? `Pick-up ${pickUp}` : null}
            </div>
          ) : null}
          {/* Ted #5 (#908) — the desk CHOOSES: apply a package day
              (default, with the balance preview) or charge the
              visit and keep the day; pick which package when the
              customer holds several. Nothing renders without one. */}
          {supportsCheckout && activePackages.length > 0 ? (
            <PackageApplyControl
              packages={activePackages}
              choice={packageChoiceByBooking[b.id] ?? DEFAULT_PACKAGE_CHOICE}
              chargeCents={b.total_cents}
              daysNeeded={packageDaysForVisit(service?.name, dogCountOf(b), stayWindowOf(b))}
              disabled={isPending}
              testIdSuffix={b.id}
              onChange={(next) =>
                setPackageChoiceByBooking((prev) => ({ ...prev, [b.id]: next }))
              }
            />
          ) : null}
          {/* Ted #11 (#914) — a boarder still here after the owner's
              grace time. The row shows nothing for an on-time pickup;
              when it IS late the desk picks one of three dispositions
              and the choice rides the checkout. */}
          {supportsCheckout ? (
            <LatePickupControl
              proposal={correctedLate.proposal}
              cleared={correctedLate.cleared}
              loading={latePickupState?.status === 'loading'}
              error={latePickupState?.status === 'error' ? latePickupState.message : null}
              onRetry={() => void reloadLatePickup(b.id)}
              choice={
                latePickupChoiceByBooking[b.id] ??
                (latePickupState?.status === 'ready'
                  ? defaultLatePickupChoice(latePickupState.proposal)
                  : { action: 'charge', length: 'full', packageId: null, waiveReason: '' })
              }
              disabled={isPending}
              testIdSuffix={b.id}
              onChange={(next) =>
                setLatePickupChoiceByBooking((prev) => ({ ...prev, [b.id]: next }))
              }
            />
          ) : null}
          {/* #7 — grooming bookings only: assign the booking to a
              groomer, which populates that groomer's queue. */}
          {isGrooming ? (
            <div style={{ marginTop: 'var(--ferro-space-2)' }}>
              <GroomerAssign
                booking={b}
                apiUrl={apiUrl}
                token={sessionToken}
                onAssigned={() => void load()}
              />
            </div>
          ) : null}
          {reversedPayment ? (
            <div
              className="ferro-banner ferro-banner--warning"
              role="status"
              style={{ marginTop: 'var(--ferro-space-2)' }}
              data-testid={`booking-payment-reversed-${b.id}`}
            >
              <p>
                Payment{' '}
                {reversedPayment === 'voided' ? 'voided' : 'refunded'}{' '}
                — this booking has no completed payment. Collect
                again or cancel it.
              </p>
              <div className="ferro-table__actions">
                <Link
                  className="ferro-btn ferro-btn--primary"
                  to="/payments"
                  data-testid={`booking-collect-${b.id}`}
                >
                  Collect payment
                </Link>
                {canCancel ? (
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--ghost"
                    disabled={isPending}
                    onClick={() => reservation.cancel(reservationTargetOf(b))}
                    data-testid={`booking-reversed-cancel-${b.id}`}
                  >
                    Cancel booking
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
        {actions.length > 0 ? (
          <div className="ferro-table__actions">
            {/* Ted #27 part 2 (#988) — the ARRIVAL half, on the rows that
                have not been checked in yet (81 of HTHD's 111). It is shown
                BEFORE the Check In button because it decides which day the
                visit is recorded on, and because the desk must be able to
                SEE and change that day: sending it invisibly would be the
                same Counter Test failure the grooming row had. Without it
                the check-in stamps today and the original-date check-out is
                then refused 422 `before_check_in`. */}
            {correctedArrivalVisible(view) && actions.includes('check-in') ? (
              <label className="ferro-field ferro-bookings__checkout-time">
                <span className="ferro-field__label">
                  Arrival — the day this visit happened
                </span>
                <input
                  type="datetime-local"
                  className="ferro-field__input"
                  value={checkinTimeOverride[b.id] ?? ''}
                  /* 09-16 re-review, the minor: the arrival had a ceiling
                     (never in the future) and no FLOOR, so a typo'd year
                     passed every guard, landed in `checked_in_at`, moved the
                     visit into that day's end-of-day report, and — because
                     `checkOutBooking`'s only floor is the check-in — widened
                     what the departure would accept. The server now refuses
                     it (`checkin_time_invalid` / `before_booking`); this is
                     that same bound in the control. */
                  min={outstandingArrivalMin(b) || undefined}
                  disabled={isPending}
                  onChange={(e) =>
                    setCheckinTimeOverride((prev) => ({
                      ...prev,
                      [b.id]: e.target.value,
                    }))
                  }
                  data-testid={`booking-checkin-time-${b.id}`}
                />
              </label>
            ) : null}
            {actions
              .filter((a) => a !== 'check-out')
              .map((a) => (
                <button
                  key={a}
                  type="button"
                  // One filled primary per ROW (Principle 2). A pending
                  // reservation's next action is Confirm, so Check In steps
                  // back to ghost there and stays primary once confirmed.
                  className={`ferro-btn ${
                    a === (b.status === 'pending' ? 'confirm' : 'check-in')
                      ? 'ferro-btn--primary'
                      : 'ferro-btn--ghost'
                  }`}
                  disabled={isPending}
                  onClick={() => {
                    // Cancel is irreversible (and cascades to a
                    // linked combo partner) — gate it behind a
                    // confirm. Check-in stays immediate. (#314)
                    // Ted #16 — undo is reversible but changes the
                    // on-site count; one confirm, focus on Keep.
                    if (a === 'cancel') {
                      reservation.cancel(reservationTargetOf(b));
                    } else if (a === 'undo-check-in') {
                      setUndoReason('');
                      setUndoConfirm({ booking: b, kind: 'check-in' });
                    } else if (a === 'undo-check-out') {
                      // Ted #22 — reopening voids an invoice and can give
                      // back a package day; it asks why, once.
                      setUndoReason('');
                      setUndoConfirm({ booking: b, kind: 'check-out' });
                    } else if (a === 'check-in') {
                      // Ted #26 (#987) — "checking in the first should
                      // prompt". ONLY when this family actually has
                      // another dog booked the same day; otherwise
                      // check-in stays exactly one tap.
                      const siblings = arrivalSiblingsOf(b);
                      if (siblings.length === 0) {
                        void handleSimpleAction(b, 'check-in');
                      } else {
                        setSiblingPrompt({ booking: b, siblings });
                      }
                    } else if (a === 'confirm') {
                      // Ted #26 — one tap when this is the household's only
                      // pending reservation; the review dialog when it is not.
                      beginConfirm(b);
                    } else if (a === 'no-show') {
                      // Ted #32c (#1158) — the dog never came: a reason, no
                      // charge, off the Expected list.
                      reservation.noShow(reservationTargetOf(b));
                    } else if (a === 'edit') {
                      // Ted #25 — opens the preview dialog. Nothing is
                      // written until the desk reads the new price and saves.
                      reservation.edit(reservationTargetOf(b));
                    } else {
                      void handleSimpleAction(b, a);
                    }
                  }}
                  data-testid={`booking-action-${a}-${b.id}`}
                >
                  {ACTION_LABEL[a]}
                </button>
              ))}
            {/* Ted 09-29 (#1111 AC6, lane LC) — "In heat" on a CHECKED-IN
                stay, for the dogs the registry's heat rule applies to (not
                known male, not known spayed/neutered, any age). Isolates her at once,
                files the incident, then arranges pickup. Never on a leg. */}
            {b.status === 'checked_in' && !b.isLeg
              ? (b.dogs ?? []).filter((dog) => heatCheckAppliesFromCard({ sex: dog.sex, altered: dog.altered })).map((dog) => (
                  <button
                    key={`in-heat-${dog.dogId}`}
                    type="button"
                    className="ferro-btn ferro-btn--ghost"
                    disabled={isPending}
                    onClick={() =>
                      inHeat.open({
                        bookingId: b.id,
                        dog: { dogId: dog.dogId, name: dog.name },
                        customerName: b.customer_name ?? `#${b.customer_id.slice(0, 8)}`,
                        // The ISOLATED pill lives in the schedule's per-dog
                        // flags, so re-read the list once she is marked.
                        onMarked: () => void load(),
                        onPickupArranged: () => {
                          // The EXISTING checkout: the row's own Check Out
                          // buttons, highlighted by the deep-link focus.
                          document.getElementById(`booking-row-${b.id}`)?.scrollIntoView({ block: 'center' });
                        },
                        onClose: () => undefined,
                      })
                    }
                    data-testid={`booking-action-in-heat-${b.id}-${dog.dogId}`}
                  >
                    In heat — {dog.name}
                  </button>
                ))
              : null}
            {/* Ted #23 (#984) — add a service to THIS visit. Offered while
                the visit is still open, including after check-in: "adding or
                changing a service after check-in should immediately update
                the appropriate calendar and staff member's schedule." Not
                offered on a row that is itself an added service (the server
                refuses a leg on a leg). */}
            {!b.isLeg &&
            (b.status === 'pending' || b.status === 'confirmed' || b.status === 'checked_in') ? (
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                disabled={isPending}
                onClick={() => setAddServiceFor(b)}
                data-testid={`booking-action-add-service-${b.id}`}
              >
                Add a service
              </button>
            ) : null}
            {supportsCheckout ? (
              <>
                {isGrooming ? (
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--primary"
                    disabled={isPending}
                    onClick={() =>
                      // Grooming routes through the modal — the
                      // settle-now/settle-later choice happens in the
                      // modal footer. Default action triggers the
                      // modal open with no pre-committed track.
                      // Lane LC — through the quiet money step first.
                      requestCheckout(b, 'settle-now')
                    }
                    data-testid={`booking-action-check-out-${b.id}`}
                  >
                    Check Out — Grooming
                  </button>
                ) : (
                  <CheckoutActions
                    disabled={isPending}
                    ariaLabel={`Checkout actions for booking ${shortId(b.id)}`}
                    testIdSuffix={b.id}
                    onChoose={(track) => requestCheckout(b, track)}
                  />
                )}
                {/* Ted #6 — "Hattie attended daycare yesterday but
                    we forgot to check her out until today." Blank
                    = now; a value = the actual departure, which
                    the server refuses if it is in the future or
                    before the check-in.

                    Ted #27 (#988), 09-16 review: a GROOMING row on the
                    Outstanding list arrives with this value pre-filled and
                    `handleGroomingCheckout` forwards it as
                    `checked_out_at` — so before this the grooming row
                    backdated the charge with NO field on screen and no way
                    to correct a refusal. The field now renders on both
                    tracks on that list. On the DAY list grooming keeps its
                    previous shape (no field, nothing seeded, server records
                    now). */}
                {correctedCheckoutVisible(view, isGrooming) ? (
                  <label className="ferro-field ferro-bookings__checkout-time">
                    <span className="ferro-field__label">
                      {/* Ted #27 (#988) — on the Outstanding list this field
                          is the POINT, not an afterthought: it is pre-filled
                          with the visit's own day and it decides which day
                          the charge belongs to. */}
                      {view === 'outstanding'
                        ? 'Check-out date — the day this visit happened'
                        : 'Actual check-out, if earlier (optional)'}
                    </span>
                    <input
                      type="datetime-local"
                      className="ferro-field__input"
                      value={checkoutTimeOverride[b.id] ?? ''}
                      disabled={isPending}
                      onChange={(e) =>
                        setCheckoutTimeOverride((prev) => ({
                          ...prev,
                          [b.id]: e.target.value,
                        }))
                      }
                      data-testid={`booking-checkout-time-${b.id}`}
                    />
                  </label>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}
      </li>
    );
  };

  return (
    <section className="ferro-admin-page" aria-busy={state.kind === 'loading'}>
      <h1 className="ferro-section__title">Bookings</h1>

      {/* Ted #27 part 2 (#988) — the second question this page answers.
          Not a fifth surface: same rows, same actions, same money path.
          The badge is the SERVER's count; loading and failure are rendered
          distinctly from zero (Principle 5 — a count we could not load must
          never read as "nothing outstanding"). */}
      <div
        className="ferro-segmented ferro-bookings__views"
        role="tablist"
        aria-label="Booking views"
      >
        <button
          type="button"
          role="tab"
          id="bookings-tab-day"
          aria-selected={view === 'day'}
          aria-controls="bookings-list"
          className={`ferro-segmented__btn${view === 'day' ? ' ferro-segmented__btn--active' : ''}`}
          onClick={() => setView('day')}
          data-testid="bookings-view-day"
        >
          By day
        </button>
        <button
          type="button"
          role="tab"
          id="bookings-tab-outstanding"
          aria-selected={view === 'outstanding'}
          aria-controls="bookings-list"
          className={`ferro-segmented__btn${view === 'outstanding' ? ' ferro-segmented__btn--active' : ''}`}
          onClick={() => setView('outstanding')}
          data-testid="bookings-view-outstanding"
        >
          Outstanding — past, not completed
          {outstanding.kind === 'ready' ? (
            <span
              className="ferro-count-badge ferro-bookings__views-badge"
              data-testid="outstanding-count-badge"
            >
              {outstanding.total}
            </span>
          ) : (
            <span
              className="ferro-count-badge ferro-bookings__views-badge ferro-bookings__views-badge--unknown"
              data-testid="outstanding-count-unknown"
              title={
                outstanding.kind === 'loading'
                  ? 'Counting the visits that were never completed…'
                  : `Count unavailable — ${outstanding.message}`
              }
              aria-label={
                outstanding.kind === 'loading'
                  ? 'Outstanding count loading'
                  : 'Outstanding count unavailable'
              }
            >
              {outstanding.kind === 'loading' ? '…' : '?'}
            </span>
          )}
        </button>
      </div>

      {view === 'outstanding' ? (
        <>
          {/* Principle 5 + Ted's own operating order: a platform check-out
              of a reservation the 06:00 MT sync still owns gets rewritten
              the next morning, so the desk completes it in Gingr FIRST.
              Saying so on the screen is the difference between a feature
              and a trap. */}
          <div
            className="ferro-banner ferro-banner--warning"
            role="status"
            data-testid="outstanding-sync-warning"
          >
            <p>
              Visits that were never checked out, oldest first. Check the visit
              out in <strong>Gingr first</strong>, then here — until the sync
              guard ships, the 6:00 AM Gingr sync rewrites a platform check-out
              for reservations from the last 30 days.
            </p>
          </div>
          <form className="ferro-toolbar" onSubmit={(e) => e.preventDefault()}>
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => void load()}
              disabled={state.kind === 'loading'}
              data-testid="outstanding-refresh"
            >
              {state.kind === 'loading' ? 'Refreshing…' : 'Refresh'}
            </button>
          </form>
        </>
      ) : (
      <form className="ferro-toolbar" onSubmit={(e) => e.preventDefault()}>
        <label className="ferro-field">
          <span className="ferro-field__label">Date</span>
          <input
            ref={dateFilterRef}
            type="date"
            className="ferro-field__input"
            value={date}
            // Ted #26 / #923 — the approval queue spans dates, so the day
            // picker has nothing to filter while it is open. Disabled rather
            // than hidden: the control does not move under the staffer's
            // finger when they switch back. Disabled-with-the-reason-attached
            // is the house rule (one of the five component states): a greyed
            // control still showing today's date, over a list spanning a year
            // and a half, is a control that is lying about what is on screen.
            disabled={pendingQueue || finding}
            aria-describedby={
              pendingQueue
                ? 'bookings-date-queue-note'
                : finding
                  ? 'bookings-date-find-note'
                  : undefined
            }
            onChange={(e) => setDate(e.target.value || todayIsoDate())}
          />
          {pendingQueue ? (
            <span
              id="bookings-date-queue-note"
              className="ferro-field__hint"
              data-testid="pending-queue-date-note"
            >
              Every date — each row shows its own.
            </span>
          ) : null}
          {finding ? (
            <span
              id="bookings-date-find-note"
              className="ferro-field__hint"
              data-testid="bookings-find-date-note"
            >
              {recentOut
                ? 'The last 30 days — each row shows its own date.'
                : 'The last two months and the next one — each row shows its own date.'}
            </span>
          ) : null}
        </label>
        {/* Ted #32c / E3.3 (#1158) — FIND A VISIT. "After checkout, those
          * options aren't easy to find": a checked-out visit used to be
          * reachable only by guessing its date. Type a dog's or owner's name
          * to read the last two months, or tap Recently checked out. */}
        <label className="ferro-field" htmlFor="bookings-search">
          <span className="ferro-field__label">Find a dog or owner</span>
          <input
            id="bookings-search"
            name="bookings-search"
            type="search"
            className="ferro-field__input"
            placeholder="Type a name…"
            autoComplete="off"
            value={searchInput}
            disabled={pendingQueue}
            onChange={(e) => setSearchInput(e.target.value)}
            data-testid="bookings-search"
          />
        </label>
        <button
          type="button"
          className={`ferro-btn ${recentOut ? 'ferro-btn--primary' : 'ferro-btn--ghost'}`}
          onClick={() => setRecentOut((v) => !v)}
          aria-pressed={recentOut}
          disabled={pendingQueue}
          data-testid="bookings-recently-checked-out"
        >
          Recently checked out
        </button>
        <label className="ferro-field">
          <span className="ferro-field__label">Service</span>
          <select
            className="ferro-field__input"
            value={serviceTypeId}
            onChange={(e) => setServiceTypeId(e.target.value)}
          >
            <option value="">All services</option>
            {serviceTypes.map((s) => (
              <option key={s.id} value={s.id}>
                {s.display_name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={() => setDate(todayIsoDate())}
          disabled={pendingQueue || finding}
        >
          Today
        </button>
        {/* Ted #26 (#987) / the approval-queue gap #923 names — every
          * reservation still waiting on a decision, oldest first, without
          * having to guess its date. */}
        <button
          type="button"
          className={`ferro-btn ${
            pendingQueue ? 'ferro-btn--primary' : 'ferro-btn--ghost'
          }`}
          onClick={() => {
            // The approval queue is its own question — a find yields to it.
            setSearchInput('');
            setSearchQuery('');
            setRecentOut(false);
            setPendingQueue((v) => !v);
          }}
          aria-pressed={pendingQueue}
          data-testid="pending-queue-toggle"
        >
          {pendingQueue ? 'Back to the day view' : 'Awaiting confirmation'}
        </button>
        <button
          type="button"
          className="ferro-btn ferro-btn--primary"
          onClick={() => void handlePrintRunCards()}
          disabled={runCardsLoading || boardingBookings.length === 0}
          data-testid="print-run-cards-button"
          aria-label={`Print run cards for ${boardingBookings.length} boarding bookings`}
        >
          {runCardsLoading
            ? 'Loading run cards…'
            : `Print Run Cards (${boardingBookings.length})`}
        </button>
      </form>
      )}

      {/* Ted #26 — what the batch actually did, on the page and not in a
        * toast: a toast that vanishes is not a record of five status
        * changes (DESIGN-PHILOSOPHY anti-patterns). Refusals are named, and
        * the TONE comes from the outcome, never from the call site: a partial
        * refusal or an outright failure is amber, and "nothing was confirmed"
        * is announced (`role="alert"`). A green banner over "Left as-is: …"
        * is read as *done* at 6:45 and the row is never revisited. */}
      {confirmFlow.banner ? (
        <div
          className={`ferro-banner ferro-banner--${confirmFlow.banner.tone}`}
          role={confirmFlow.banner.role}
          data-testid="confirm-outcome-banner"
          data-tone={confirmFlow.banner.tone}
        >
          <p>{confirmFlow.banner.text}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={confirmFlow.dismissBanner}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {confirmFlow.dialogProps ? (
        <ConfirmPendingDialog {...confirmFlow.dialogProps} />
      ) : null}

      {actionError ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{actionError}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => setActionError(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {/* #905/#987 review fix — a household check-in that worked says so.
        * Status tone, not the red error banner: before this, a partial
        * success put "Checked in <dog>." inside error chrome, and a FULL
        * success said nothing at all — the card simply vanished. */}
      {checkInNotice ? (
        <div
          className="ferro-notice"
          role="status"
          data-testid="household-check-in-notice"
        >
          <p>{checkInNotice}</p>
          <div className="ferro-notice__actions">
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => setCheckInNotice(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {/* Ted #25 — an edit's outcome must live somewhere inspectable, not
        * only in a toast that vanishes (DESIGN-PHILOSOPHY anti-pattern).
        * Ted #32c — the same banner now says what a No-show or a Cancel from
        * the shared dialogs did, in the same place. */}
      {reservation.banner}

      {/* Issue #140 — non-blocking package-redeem notice (the checkout
        * already succeeded; this only reports that the day couldn't be
        * applied, e.g. an insufficient balance). */}
      {packageNotice ? (
        <div
          className="ferro-notice ferro-notice--warning"
          role="status"
          data-testid="package-notice"
        >
          <p>{packageNotice}</p>
          <div className="ferro-notice__actions">
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => setPackageNotice(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {/* Issue #251 — non-blocking money-path confirmations (invoice queued
        * for end-of-day, covered-invoice settle hiccup). Status tone, not
        * error chrome (review nit — was borrowing ferro-dashboard__error). */}
      {settleNotice ? (
        <div
          className="ferro-notice"
          role="status"
          data-testid="settle-notice"
        >
          <p>{settleNotice}</p>
          <div className="ferro-notice__actions">
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => setSettleNotice(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {/* #251 should-fix 7 — INDETERMINATE package-redeem outcome. A real
        * alert (money may be wrong in either direction); staff must verify
        * the package balance before collecting anything. */}
      {redeemIndeterminate ? (
        <div
          className="ferro-dashboard__error"
          role="alert"
          data-testid="redeem-indeterminate-notice"
        >
          <p>{redeemIndeterminate}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => setRedeemIndeterminate(null)}
          >
            Dismiss — I will verify before charging
          </button>
        </div>
      ) : null}

      {state.kind === 'loading' ? (
        <p className="ferro-muted" role="status">
          Loading bookings…
        </p>
      ) : null}

      {state.kind === 'error' ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{state.message}</p>
          {state.forbidden ? (
            // A permission wall — retrying won't change the role. Offer a way
            // back to a surface this staffer CAN use instead of a dead Retry.
            <Link className="ferro-btn ferro-btn--ghost" to="/">
              Back to dashboard
            </Link>
          ) : (
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => void load()}
            >
              Retry
            </button>
          )}
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        sorted.length === 0 ? (
          view === 'outstanding' ? (
            // Empty state teaches, and says what "nothing" means here
            // (Principle 2). This is the GOOD outcome, not a dead end.
            <p className="ferro-muted" data-testid="outstanding-empty">
              Nothing outstanding — every visit before today has been checked
              out, cancelled or marked a no-show. Forgotten visits land here
              automatically; there is nothing to set up.
            </p>
          ) : finding ? (
            // Ted #32c / E3.3 — say what was looked at, so "nothing" is a
            // finding and not a broken search (Principle 5).
            <p className="ferro-muted" data-testid="bookings-find-empty">
              {recentOut
                ? searchActive
                  ? `No visit checked out in the last 30 days matches “${searchQuery}”.`
                  : 'No visit was checked out in the last 30 days.'
                : `No visit in the last two months or the next one matches “${searchQuery}”.`}
            </p>
          ) : (
            <p className="ferro-muted" data-testid="bookings-empty">
              {pendingQueue
                ? 'Nothing is waiting on a decision — every reservation has been confirmed, checked in or cancelled.'
                : 'No bookings for this date.'}
            </p>
          )
        ) : (
          <ul
            className="ferro-table"
            id="bookings-list"
            role="tabpanel"
            aria-labelledby={
              view === 'outstanding' ? 'bookings-tab-outstanding' : 'bookings-tab-day'
            }
            aria-label={
              view === 'outstanding'
                ? 'Outstanding reservations, oldest first'
                : 'Booking list'
            }
          >
            {displayEntries.map((entry) =>
              entry.kind === 'row' ? (
                renderRow(entry.booking)
              ) : entry.kind === 'arrival-household' ? (
                (() => {
                  const cid = entry.household.customerId;
                  const bookings = entry.household.rows.map((r) => r.booking);
                  const visits = entry.household.rows.length;
                  const dogs = arrivalDogCount(bookings);
                  const countLabel = arrivalCountLabel(bookings);
                  const busy = householdCheckInBusy.has(cid);
                  const rowBusy = entry.household.rows.some((r) =>
                    pending.has(r.bookingId),
                  );
                  const filteredService = serviceTypeId
                    ? (serviceTypeById.get(serviceTypeId)?.display_name ?? null)
                    : null;
                  return (
                    <li
                      key={`arrival-household-${cid}`}
                      className="ferro-table__row ferro-household ferro-household--arrivals"
                      data-testid={`arrival-household-${cid}`}
                    >
                      {/* Ted #2 follow-up (#905) — one owner's dogs still to
                          check in, ONE "Check in all". The rows below stay
                          individually actionable, and a single row's Check In
                          still offers the #987 prompt. Grooming arrivals are
                          included on purpose — see `isArrivalStatus`. */}
                      <div className="ferro-household__head">
                        <div>
                          <div className="ferro-household__names">
                            {arrivalHouseholdLabel(entry.household)}
                          </div>
                          <div
                            className="ferro-household__meta"
                            data-testid={`arrival-household-meta-${cid}`}
                          >
                            {/* Dogs and visits are different numbers when a
                                booking carries more than one dog; say both
                                rather than let one stand in for the other. */}
                            {dogs !== null && dogs !== visits
                              ? `${countLabel} across ${visits} visits`
                              : `${visits} visits`}{' '}
                            · not checked in yet
                            {/* Ted #27 part 2 (#988), 09-16 re-review — on
                                the Outstanding list this card is NOT a day.
                                Two of an owner's forgotten visits can be
                                weeks apart, and each is checked in on its
                                own original date (the arrival field on each
                                row below). Say so rather than let "2 dogs,
                                not checked in yet" read as one morning. */}
                            {view === 'outstanding'
                              ? ' · each on its own original date'
                              : ''}
                            {filteredService
                              ? ` · ${filteredService} bookings only`
                              : ''}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="ferro-btn ferro-btn--primary"
                          disabled={busy || rowBusy}
                          aria-busy={busy}
                          onClick={() => setHouseholdPicker(entry.household)}
                          data-testid={`household-check-in-all-${cid}`}
                          aria-label={
                            busy
                              ? `Checking in ${countLabel}: ${arrivalHouseholdLabel(entry.household)}`
                              : `Check in all ${countLabel}: ${arrivalHouseholdLabel(entry.household)}`
                          }
                        >
                          {busy
                            ? `Checking in ${countLabel}…`
                            : `Check in all ${countLabel}`}
                        </button>
                      </div>
                      {/* A disabled control always says why (#905/#987
                          review): the batch is blocked only while one of
                          these dogs is mid-action on its own row. */}
                      {rowBusy && !busy ? (
                        <p
                          className="ferro-household__hint"
                          data-testid={`arrival-household-blocked-${cid}`}
                        >
                          One of these dogs is mid-action on its own row —
                          finishing that first.
                        </p>
                      ) : null}
                      <ul
                        className="ferro-table ferro-household__rows"
                        aria-label={`Arrivals for ${arrivalHouseholdLabel(entry.household)}`}
                      >
                        {entry.household.rows.map((r) => renderRow(r.booking))}
                      </ul>
                    </li>
                  );
                })()
              ) : (
                <li
                  key={`household-${entry.household.customerId}`}
                  className="ferro-table__row ferro-household"
                  data-testid={`household-${entry.household.customerId}`}
                >
                  {/* Ted #10 (#913) — one owner's checked-in dogs, ONE
                      "Check out all". The rows below stay individually
                      actionable; Sawyer C3 — a groom is in the card and its
                      tier is confirmed in a step when the button is pressed. */}
                  <div className="ferro-household__head">
                    <div>
                      <div className="ferro-household__names">
                        {householdLabel(entry.household)}
                      </div>
                      <div
                        className="ferro-household__meta"
                        data-testid={`household-meta-${entry.household.customerId}`}
                      >
                        {entry.household.rows.length} visits ·{' '}
                        {householdMoneyLabel(
                          householdDueAfterPackages(
                            entry.household.rows.map((r) => ({
                              id: r.id,
                              customerId: r.customer_id,
                              serviceName: serviceTypeById.get(r.service_type_id)?.name ?? null,
                              totalCents: r.total_cents,
                              dogCount: dogCountOf(r),
                              stay: stayWindowOf(r),
                            })),
                            activePackageByKey,
                            (id) => packageChoiceByBooking[id] ?? DEFAULT_PACKAGE_CHOICE,
                          ),
                        )}
                        {/* Sawyer 13 household-checkout-grooming (C3) */}
                        {(() => {
                          const note = householdGroomingNote(
                            entry.household,
                            (r) => joinNames(r.dog_names ?? []) || `#${shortId(r.id)}`,
                          );
                          return note ? ` ${note}` : '';
                        })()}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--primary"
                      disabled={
                        entry.household.rows.some((r) => pending.has(r.id)) ||
                        householdTierSteps?.household.customerId === entry.household.customerId
                      }
                      onClick={() => requestHouseholdCheckout(entry.household)}
                      data-testid={`household-check-out-all-${entry.household.customerId}`}
                    >
                      Check out all ({entry.household.rows.length})
                    </button>
                  </div>
                  <ul
                    className="ferro-table ferro-household__rows"
                    aria-label={`Visits for ${householdLabel(entry.household)}`}
                  >
                    {entry.household.rows.map(renderRow)}
                  </ul>
                </li>
              ),
            )}
          </ul>
        )
      ) : null}

      {/* Ted #27 part 2 (#988), 09-16 review: the badge is the server's
          unbounded `total` while the list holds at most one page, so at 201+
          outstanding rows the badge said 250 and the list showed 200 with
          nothing on screen admitting it. "One glance, one truth" means the
          screen has to say when it is not showing everything. HTHD's backlog
          is 111 today, so this is a guard rather than a live state. */}
      {state.kind === 'ready' &&
      view === 'outstanding' &&
      outstanding.kind === 'ready' &&
      outstanding.total > sorted.length ? (
        <p className="ferro-muted" data-testid="outstanding-truncated">
          Showing the oldest {sorted.length} of {outstanding.total}. Complete or
          cancel these and refresh to see the rest.
        </p>
      ) : null}

      {runCardsError ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{runCardsError}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => setRunCardsError(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {tierConfirm ? (
        <TierConfirmation
          open={true}
          onClose={() => setTierConfirm(null)}
          onConfirm={handleTierConfirm}
          bookingLabel={tierConfirm.bookingLabel}
          existingTotalCents={tierConfirm.booking.total_cents}
          originalTierKey={tierConfirm.originalTierKey}
          submitting={tierConfirm.submitting}
          requestedAddOns={requestedAddOns}
        />
      ) : null}

      {/* Sawyer 13 household-checkout-grooming (C3) — one tier step per groom
          inside "Check out all". Keyed by booking so each step starts at its
          own booked tier with an empty note (refuter 8). */}
      {householdTierSteps && householdTierSteps.queue[householdTierSteps.index]
        ? (() => {
            const groom = householdTierSteps.queue[householdTierSteps.index]!;
            return (
              <TierConfirmation
                key={groom.id}
                open={true}
                mode="household-step"
                step={{
                  index: householdTierSteps.index + 1,
                  count: householdTierSteps.queue.length,
                  dogLabel: joinNames(groom.dog_names ?? []) || `#${shortId(groom.id)}`,
                }}
                onClose={cancelHouseholdTierSteps}
                onConfirm={confirmHouseholdTierStep}
                bookingLabel={buildBookingLabel(groom)}
                existingTotalCents={groom.total_cents}
                originalTierKey={extractBookedTierKey(groom)}
                requestedAddOns={requestedAddOns}
              />
            );
          })()
        : null}

      {/* Issue #251 — settle-now payment collection. The open-invoice money
        * record ALREADY exists when this opens; dismissing degrades safely
        * to settle-later (the invoice stays in the end-of-day queue). */}
      {settleDialog ? (
        <SettleNowDialog
          apiUrl={apiUrl}
          bookingId={settleDialog.booking.id}
          bookingLabel={settleDialog.bookingLabel}
          customerId={settleDialog.booking.customer_id}
          invoiceId={settleDialog.invoice.id}
          totalCents={settleDialog.invoice.totalCents}
          idempotencyKey={settleDialog.idempotencyKey}
          household={settleDialog.household}
          onClose={(result) => {
            setSettleDialog(null);
            if (result === 'queued') {
              setSettleNotice(
                `Booking ${settleDialog.bookingLabel} checked out — the invoice stays in the end-of-day queue (Payments page) until it is settled.`,
              );
            }
          }}
        />
      ) : null}

      {/* Issue #251 — BLOCKING failure surface: checked out, but the
        * money record could not be written. Retry re-POSTs the exact same
        * record; dismissing requires an explicit acknowledgment. */}
      {moneyRecordFailure ? (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-label="Payment record failed"
          data-testid="money-record-failure"
          className="ferro-blocking-dialog__overlay"
        >
          <div className="ferro-blocking-dialog__panel">
            <h2>Checked out — payment NOT recorded</h2>
            <p>
              Booking {moneyRecordFailure.bookingLabel} was checked out, but
              the invoice could not be written to the payments queue (
              {moneyRecordFailure.error}). Until this is recorded, the visit
              is NOT in the money funnel.
            </p>
            <div className="ferro-blocking-dialog__actions">
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => {
                  setMoneyRecordFailure(null);
                  setSettleNotice(
                    `UNRECORDED VISIT: booking ${moneyRecordFailure.bookingLabel} (${formatMoney(moneyRecordFailure.totalCents)}) checked out with NO payment record — record it manually in the Payments console.`,
                  );
                }}
                data-testid="money-record-dismiss"
              >
                Dismiss — I’ll record it manually
              </button>
              <button
                type="button"
                className="ferro-btn ferro-btn--primary"
                onClick={() => {
                  const retry: MoneyRecordArgs = moneyRecordFailure;
                  setMoneyRecordFailure(null);
                  void recordMoneyLeg(retry);
                }}
                data-testid="money-record-retry"
              >
                Retry recording
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* #314 — irreversible-cancel confirm · Ted #25 (#986) — edit the
        * reservation instead of cancelling and recreating it · Ted #32c
        * (#1158) — mark a no-show. One dialog at a time, shared with the Front
        * Desk and dashboard Expected rows (components/booking/). */}
      {reservation.dialogs}
      {seriesView ? <RecurringSeriesPanel apiUrl={apiUrl} seriesId={seriesView} onClose={() => setSeriesView(null)} /> : null}

      {addServiceFor ? (
        <AddServiceDialog
          apiUrl={apiUrl}
          token={sessionToken}
          bookingId={addServiceFor.id}
          bookingLabel={`${(addServiceFor.dog_names ?? []).join(', ') || shortId(addServiceFor.id)} — ${addServiceFor.customer_name ?? ''}`.trim()}
          parentCheckedIn={addServiceFor.status === 'checked_in'}
          onClose={() => setAddServiceFor(null)}
          onAdded={() => void load()}
        />
      ) : null}

      {undoConfirm ? (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-label={
            undoConfirm.kind === 'check-in'
              ? 'Confirm undoing the check-in'
              : 'Confirm reopening the checkout'
          }
          data-testid={
            undoConfirm.kind === 'check-in'
              ? 'undo-check-in-confirm-dialog'
              : 'undo-check-out-confirm-dialog'
          }
          className="ferro-blocking-dialog__overlay"
        >
          <div className="ferro-blocking-dialog__panel">
            <h2>
              {undoConfirm.kind === 'check-in'
                ? 'Undo this check-in?'
                : 'Reopen this checkout?'}
            </h2>
            {undoConfirm.kind === 'check-in' ? (
              <p>
                {buildBookingLabel(undoConfirm.booking)} goes back to Expected
                with its check-in time cleared. The mistake is kept on the audit
                trail; nothing else is lost.
              </p>
            ) : (
              <p>
                {buildBookingLabel(undoConfirm.booking)} goes back to Checked in
                and the unpaid invoice from this checkout is voided, so you can
                adjust the price and check out again. A package day this
                checkout used is given back. Already paid? Void or refund the
                payment in Payments first.
              </p>
            )}
            {undoConfirm.kind === 'check-out' ? (
              <label className="ferro-field">
                <span className="ferro-field__label">Why? (required)</span>
                <input
                  type="text"
                  className="ferro-field__input"
                  value={undoReason}
                  onChange={(e) => setUndoReason(e.target.value)}
                  placeholder="e.g. missed the multi-dog discount"
                  maxLength={200}
                  data-testid="undo-check-out-reason"
                />
              </label>
            ) : null}
            <div className="ferro-blocking-dialog__actions">
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                ref={undoDismissRef}
                onClick={() => setUndoConfirm(null)}
                data-testid={
                  undoConfirm.kind === 'check-in'
                    ? 'undo-check-in-dismiss'
                    : 'undo-check-out-dismiss'
                }
              >
                {undoConfirm.kind === 'check-in'
                  ? 'Keep checked in'
                  : 'Keep it checked out'}
              </button>
              <button
                type="button"
                className="ferro-btn ferro-btn--primary"
                disabled={
                  undoConfirm.kind === 'check-out' && undoReason.trim().length < 2
                }
                onClick={() => {
                  const target = undoConfirm.booking;
                  const kind = undoConfirm.kind;
                  const reason = undoReason.trim();
                  setUndoConfirm(null);
                  void handleSimpleAction(
                    target,
                    kind === 'check-in' ? 'undo-check-in' : 'undo-check-out',
                    reason,
                  );
                }}
                data-testid={
                  undoConfirm.kind === 'check-in'
                    ? 'undo-check-in-accept'
                    : 'undo-check-out-accept'
                }
              >
                {undoConfirm.kind === 'check-in'
                  ? 'Undo check-in'
                  : 'Reopen checkout'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Ted #28 (#1050) — "Is Bella here?" for a transported arrival. */}
      {arrivalDialog}
      {/* Lane LC — "Before you check in", the quiet checkout step, "In heat". */}
      {interruptDialog}
      {checkoutInterruptDialog}
      {inHeat.dialog}
      {/* Ted #32c (#1160) — the shared family picker: ONLY the tapped dog goes
          in unless the desk ticks a sibling. Escape / "Don't check anyone in"
          close it with nothing posted; the focused control is that no-op. */}
      {siblingPrompt ? (
        <FamilyCheckInDialog
          key={siblingPrompt.booking.id}
          anchor={familyMemberFromBooking(siblingPrompt.booking, serviceTypeById)}
          siblings={siblingPrompt.siblings.map((sib) => familyMemberFromBooking(sib, serviceTypeById))}
          ownerLabel={
            siblingPrompt.booking.customer_name ??
            `#${siblingPrompt.booking.customer_id.slice(0, 8)}`
          }
          startTicked="anchor-only"
          // Review fix (#905/#987): the list this prompt is built from is the
          // FILTERED schedule, so "today" is only the whole day when nothing is
          // filtered. Say so rather than assert a fact the data cannot support.
          scopeNote={
            serviceTypeId && serviceTypeById.get(serviceTypeId)
              ? `Only ${serviceTypeById.get(serviceTypeId)!.display_name} bookings are listed right now — this family may have other services booked today that the filter is hiding.`
              : null
          }
          onClose={() => setSiblingPrompt(null)}
          onConfirm={(ids) => {
            const prompt = siblingPrompt;
            setSiblingPrompt(null);
            const picked = [prompt.booking, ...prompt.siblings].filter((b) => ids.includes(b.id));
            if (picked.length <= 1) {
              void handleSimpleAction(prompt.booking, 'check-in');
              return;
            }
            void handleHouseholdCheckIn(picked.map(toArrivalRow));
          }}
        />
      ) : null}
      {/* Ted #26 / Item 24 — "Check in all" asks first, like Front Desk and the
          dashboard: the whole family starts ticked, a dog that isn't here yet
          is one tap from unticked, and "Don't check anyone in" posts nothing. */}
      {householdPicker ? (
        <FamilyCheckInDialog
          key={`household-${householdPicker.customerId}`}
          anchor={null}
          siblings={householdPicker.rows.map((r) => familyMemberFromBooking(r.booking, serviceTypeById))}
          ownerLabel={householdPicker.customerName}
          startTicked="all"
          scopeNote={
            serviceTypeId && serviceTypeById.get(serviceTypeId)
              ? `Only ${serviceTypeById.get(serviceTypeId)!.display_name} bookings are listed right now — this family may have other services booked today that the filter is hiding.`
              : null
          }
          onClose={() => setHouseholdPicker(null)}
          onConfirm={(ids) => {
            const household = householdPicker;
            setHouseholdPicker(null);
            const picked = household.rows.filter((r) => ids.includes(r.bookingId));
            if (picked.length === 0) return;
            void handleHouseholdCheckIn(picked);
          }}
        />
      ) : null}

      {runCardPrompt.element}
      {ownerNotes.element}
      {runCardsView ? (
        // Portals itself to <body> — print.css only prints a direct body
        // child, so the overlay must not live inside this page's tree.
        <RunCardPrintLayout
          cards={runCardsView.cards}
          dateLabel={runCardsView.dateLabel}
          onClose={() => setRunCardsView(null)}
          onPrint={recordDeckPrinted}
          onUseLive={() => void loadLiveDeck()}
          // Sawyer 06 beds-lockers-runcard (B4, refuter #1) — "Edit card" per card; after a save the deck reloads from the server.
          edit={readSession().token ? { apiUrl, token: readSession().token ?? '', night: date, reload: async () => { const { token } = readSession(); if (!token) return; const live = runCardsView.live === true; const { cards: apiCards } = await fetchRunCards(apiUrl, token, date, { live }); setRunCardsView((v) => (v ? { ...v, cards: apiCards.map(apiCardToRunCard) } : v)); } } : undefined}
        />
      ) : null}

      {packageSummary ? (
        <div
          data-testid="package-summary-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(47, 62, 70, 0.48)',
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--ferro-space-4)',
          }}
        >
          <button
            type="button"
            aria-label="Close package balance summary"
            data-testid="package-summary-backdrop"
            onClick={closePackageSummary}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              padding: 0,
              border: 0,
              background: 'transparent',
              cursor: 'default',
            }}
          />
          <div
            ref={packageSummaryDialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Package balance after checkout"
            data-testid="package-summary-dialog"
            onKeyDown={handlePackageSummaryKeyDown}
            style={{
              position: 'relative',
              background: 'var(--brand-background, #fffaf3)',
              borderRadius: 'var(--ferro-radius)',
              padding: 'var(--ferro-space-5)',
              maxWidth: '420px',
              width: '100%',
              boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--ferro-space-3)',
            }}
          >
            <h2 style={{ margin: 0 }}>Package applied</h2>
            <PackageBalance
              pkg={packageSummary.pre}
              variant="post-checkout"
              onSendCourtesyReminder={() => setReminderSent(true)}
            />
            {reminderSent ? (
              <p
                className="ferro-muted"
                data-testid="package-summary-reminder-sent"
                role="status"
              >
                Courtesy reminder queued. Becky will follow up before they run
                out.
              </p>
            ) : shouldOfferCourtesyReminder(packageSummary.post) ? (
              <p className="ferro-muted">
                Balance is at {packageSummary.post.remainingDays} day
                {packageSummary.post.remainingDays === 1 ? '' : 's'} — offer
                a refill before they run out.
              </p>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                ref={packageSummaryCloseRef}
                className="ferro-btn ferro-btn--primary"
                onClick={closePackageSummary}
                data-testid="package-summary-close"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Ted #26 (#987) — what the desk must be able to read BEFORE it approves a
 * sibling in the batch: the service and when the dog is expected. Never a
 * raw service-type id — an unknown service reads "Booked visit".
 */
export function siblingSummary(
  booking: AdminBooking,
  serviceTypes: ReadonlyMap<string, ServiceTypeSummary>,
): string {
  const service = serviceTypes.get(booking.service_type_id);
  const parts: string[] = [
    service?.display_name || service?.name || 'Booked visit',
  ];
  const window =
    booking.drop_off_time && booking.pick_up_time
      ? `${booking.drop_off_time}–${booking.pick_up_time}`
      : booking.drop_off_time ?? booking.pick_up_time ?? booking.start_time;
  if (window) parts.push(window);
  return parts.join(' · ');
}

/**
 * Ted #32c (#1160) — one booking row as the shared family picker shows it: the
 * dogs, how many, the service and times, and the reservation's note. The same
 * words `siblingSummary` has always used, so the picker reads the same on every
 * surface.
 */
function familyMemberFromBooking(
  booking: AdminBooking,
  serviceTypes: ReadonlyMap<string, ServiceTypeSummary>,
): FamilyCheckInMember {
  return {
    bookingId: booking.id,
    dogLabel: joinNames(booking.dog_names ?? []) || buildBookingLabel(booking),
    dogCount: Math.max(1, booking.dog_names?.length ?? 1),
    summary: siblingSummary(booking, serviceTypes),
    note: booking.notes,
  };
}

/**
 * Ted #25 — the dialog header names the DOGS, falling back to the owner and
 * only then to a short id (showing a raw uuid to a receptionist is on the
 * DESIGN-PHILOSOPHY anti-pattern list).
 */
export function bookingDisplayLabel(booking: AdminBooking): string {
  if (booking.dog_names && booking.dog_names.length > 0) {
    return booking.customer_name
      ? `${booking.dog_names.join(', ')} — ${booking.customer_name}`
      : booking.dog_names.join(', ');
  }
  return booking.customer_name ?? `#${booking.id.slice(0, 8)}`;
}

function buildBookingLabel(booking: AdminBooking): string {
  const parts: string[] = [];
  if (booking.start_time) parts.push(booking.start_time);
  parts.push(`#${booking.id.slice(0, 8)}`);
  return parts.join(' · ');
}

// ---------- Household checkout (Ted #10 · issue #913) ----------

/** "Hazel", "Hazel & Hank", "Hazel, Hank & Skipper". */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

/** Every dog across the rows, once each, in row order. */
export function uniqueDogNames(rows: readonly AdminBooking[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const b of rows) {
    for (const name of b.dog_names ?? []) {
      if (seen.has(name)) continue;
      seen.add(name);
      out.push(name);
    }
  }
  return out;
}

/** "Hazel, Hank & Skipper — Jodie Royer" (the owner when no dog names). */
export function householdLabel(h: {
  readonly dogNames: readonly string[];
  readonly customerName: string | null;
  readonly customerId: string;
}): string {
  const dogs = joinNames(h.dogNames);
  const owner = h.customerName ?? `#${h.customerId.slice(0, 8)}`;
  return dogs ? `${dogs} — ${owner}` : owner;
}

/**
 * Pull the booked tier key off the booking record. Today the API doesn't
 * carry an explicit `grooming_tier` field on AdminBooking, so we default
 * to `basic`. When Track-A surfaces the field (via `groomingSubServiceId`
 * or an explicit tier column), wire it here.
 */
function extractBookedTierKey(_booking: AdminBooking): GroomingTierKey | null {
  // Forward-compat: when AdminBooking grows a `grooming_tier` field, return
  // it. Today, defaulting to `basic` matches the Yogi-class case from
  // Becky's walkthrough (booked basic, checked out as profile).
  return 'basic';
}
